import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Gamepad2, Play, Save, ArrowLeft, Send, Box, Lightbulb, Eye, ChevronLeft,
  Loader2, ChevronRight, ChevronDown, Trash2, Plus, Settings2,
  Move, RotateCcw, Maximize2, Palette, Bot, User, Sparkles,
  Globe, Share2, Check, Download, Home
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import * as BABYLON from '@babylonjs/core';
import '@babylonjs/loaders';
import { GridMaterial } from '@babylonjs/materials/grid';
import AssetsPanel from '../components/AssetsPanel';
import TouchCoachMark from '../components/TouchCoachMark';
import EditorTour from '../components/EditorTour';
import SpriteTimeline from '../components/SpriteTimeline';
import TilemapPainter from '../components/TilemapPainter';
import { buildObjectMaterial, detectMaterialChannels } from '../lib/material';
import { roleSpriteDataUrl } from '../lib/roleSprites';
import { removeBackgroundIfNeeded } from '../lib/bgRemove';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Object type icons
const objectIcons = {
  player: '🎮',
  platform: '⬜',
  floor: '⬜',
  wall: '🧱',
  terrain: '🌍',
  enemy: '👾',
  npc: '🧑',
  collectible: '⭐',
  structure: '🏠',
  vehicle: '🚗',
  puzzle_piece: '🧩',
  goal: '🎯',
  light: '💡',
  trigger: '⚡',
  cube: '◼', cylinder: '⬤', sphere: '●', cone: '▲', torus: '◯', capsule: '⬭', plane: '▭'
};

// One scale space for editor + runtime: geometry is authored at unit size and scaled by this.
const ACTOR_SIZES = { player: 2.2, enemy: 2.0, npc: 2.0, collectible: 1.1, goal: 2.6, vehicle: 2.4, decoration: 1.6 };
function effectiveScale(obj) {
  const s = obj.scale;
  if (obj.scale_abs && s) return s;
  switch (obj.type) {
    case 'player': case 'enemy': case 'npc': case 'collectible': case 'goal': case 'vehicle': case 'decoration': {
      const v = (s && s[0] > 1) ? s[0] : ACTOR_SIZES[obj.type];
      return [v, v, v];
    }
    case 'sprite': { const v = (s && s[0]) || 2.5; return [v, v, v]; }
    case 'background': return [((s && s[0]) || 10) * 2, (s && s[1]) || 5, 1];
    case 'texture_prop': { const v = (s && s[0]) || 2; return [v, v, v]; }
    default: return s || [1, 1, 1];
  }
}

export default function Editor() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedObject, setSelectedObject] = useState(null);
  const [expandedObjects, setExpandedObjects] = useState({});
  // Hierarchy grouping + inline rename
  const [multiSel, setMultiSel] = useState([]);
  const [renamingObj, setRenamingObj] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [renamingGroup, setRenamingGroup] = useState(null);
  const [renameGroupVal, setRenameGroupVal] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  
  // AI Copilot
  const [copilotMessages, setCopilotMessages] = useState([]);
  const [copilotInput, setCopilotInput] = useState('');
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [copilotExpanded, setCopilotExpanded] = useState(false);
  const copilotScrollRef = useRef(null);
  // Auto-scroll the copilot chat to the newest message so users see their typing land immediately.
  useEffect(() => {
    if (copilotScrollRef.current) {
      copilotScrollRef.current.scrollTop = copilotScrollRef.current.scrollHeight;
    }
    // Auto-expand the panel when there is activity (typing or a response arriving)
    if (copilotMessages.length > 0 || copilotLoading) {
      setCopilotExpanded(true);
    }
  }, [copilotMessages, copilotLoading]);
  
  // Autosave — opt-in, silent, every 60s
  const [autosave, setAutosave] = useState(false);
  const saveFnRef = useRef(null);
  useEffect(() => {
    if (!autosave) return;
    const iv = setInterval(() => { saveFnRef.current && saveFnRef.current(true); }, 60000);
    return () => clearInterval(iv);
  }, [autosave]);

  // Gizmo mode: 'position', 'rotation', 'scale'
  const [gizmoMode, setGizmoMode] = useState('position');
  const [publishing, setPublishing] = useState(false);
  // Mini playtest window — live iframe of the runtime inside the editor
  const [playtestOpen, setPlaytestOpen] = useState(false);
  const [playtestKey, setPlaytestKey] = useState(0);
  // Multi-level editing: which stage's objects are on the canvas right now
  const [editingStage, setEditingStage] = useState(0);
  const editingStageRef = useRef(0);
  useEffect(() => { editingStageRef.current = editingStage; }, [editingStage]);

  const getStages = () => project?.game_data?.projectDefinition?.stages || [];

  const switchStage = (to) => {
    if (to === editingStage) return;
    pushUndo();
    setProject(prev => {
      const gd = prev.game_data || {};
      const pd = { ...(gd.projectDefinition || {}) };
      const stages = [...(pd.stages || [{ name: 'Level 1' }])];
      stages[editingStage] = { ...(stages[editingStage] || {}), objects: gd.objects || [] };
      pd.stages = stages;
      return { ...prev, game_data: { ...gd, projectDefinition: pd, objects: stages[to]?.objects || [] } };
    });
    setSelectedObject(null);
    setMultiSel([]);
    setEditingStage(to);
  };

  const addStage = () => {
    pushUndo();
    const newIdx = Math.max(1, getStages().length || 1);
    setProject(prev => {
      const gd = prev.game_data || {};
      const pd = { ...(gd.projectDefinition || {}) };
      const stages = [...(pd.stages || [])];
      if (!stages.length) stages.push({ name: 'Level 1', objects: gd.objects || [] });
      else stages[editingStage] = { ...(stages[editingStage] || {}), objects: gd.objects || [] };
      const cloned = JSON.parse(JSON.stringify(gd.objects || []));
      stages.push({ name: `Level ${stages.length + 1}`, story: '', objects: cloned });
      pd.stages = stages;
      return { ...prev, game_data: { ...gd, projectDefinition: pd, objects: cloned } };
    });
    setSelectedObject(null);
    setMultiSel([]);
    setEditingStage(newIdx);
    toast.success(`Level ${newIdx + 1} created (a copy of the current level as a starting point)`);
  };

  const removeStage = () => {
    const stages = getStages();
    if (stages.length <= 1 || editingStage === 0) { toast.error('Level 1 cannot be removed'); return; }
    if (!window.confirm(`Delete "${stages[editingStage]?.name || `Level ${editingStage + 1}`}" and everything in it?`)) return;
    pushUndo();
    setProject(prev => {
      const gd = prev.game_data || {};
      const pd = { ...(gd.projectDefinition || {}) };
      const st = [...(pd.stages || [])];
      st.splice(editingStage, 1);
      pd.stages = st;
      return { ...prev, game_data: { ...gd, projectDefinition: pd, objects: st[0]?.objects || gd.objects || [] } };
    });
    setSelectedObject(null);
    setEditingStage(0);
    toast.success('Level removed');
  };

  const reorderStage = (from, to) => {
    const stages = getStages();
    if (from === to || from < 0 || to < 0 || from >= stages.length || to >= stages.length) return;
    pushUndo();
    const cur = editingStage;
    let newIdx = cur;
    if (cur === from) newIdx = to;
    else if (from < cur && to >= cur) newIdx = cur - 1;
    else if (from > cur && to <= cur) newIdx = cur + 1;
    setProject(prev => {
      const gd = prev.game_data || {};
      const pd = { ...(gd.projectDefinition || {}) };
      const st = [...(pd.stages || [])];
      st[cur] = { ...(st[cur] || {}), objects: gd.objects || [] };
      const [moved] = st.splice(from, 1);
      st.splice(to, 0, moved);
      pd.stages = st;
      return { ...prev, game_data: { ...gd, projectDefinition: pd, objects: st[newIdx]?.objects || gd.objects || [] } };
    });
    setEditingStage(newIdx);
    toast.success(`Level moved to position ${to + 1}`);
  };

  const updateStageMeta = (field, value) => {
    setProject(prev => {
      const gd = prev.game_data || {};
      const pd = { ...(gd.projectDefinition || {}) };
      const stages = [...(pd.stages || [{ name: 'Level 1', objects: gd.objects || [] }])];
      stages[editingStage] = { ...(stages[editingStage] || {}), [field]: value };
      pd.stages = stages;
      return { ...prev, game_data: { ...gd, projectDefinition: pd } };
    });
  };
  
  // Babylon.js refs
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const sceneRef = useRef(null);
  const meshMapRef = useRef({});
  const gizmoManagerRef = useRef(null);
  const meshRegistryRef = useRef(new Map());

  // Load project
  useEffect(() => {
    fetchProject();
    fetchCopilotHistory();
  }, [projectId]);

  // Initialize Babylon.js — runs exactly once per projectId. `project` is read via a ref so
  // property tweaks never dispose/recreate the engine (fixes the blink/rebound bug).
  const initializedRef = useRef(null);
  const projectRef = useRef(null);
  const [projectReady, setProjectReady] = useState(false);
  useEffect(() => { projectRef.current = project; if (project && !projectReady) setProjectReady(true); }, [project, projectReady]);
  useEffect(() => {
    if (!canvasRef.current || !projectId || !projectReady) return;
    if (initializedRef.current === projectId) return;
    initializedRef.current = projectId;
    const project = projectRef.current;

    // Create engine
    const engine = new BABYLON.Engine(canvasRef.current, true, { preserveDrawingBuffer: true, stencil: true });
    engineRef.current = engine;

    // Create scene
    const scene = new BABYLON.Scene(engine);
    scene.clearColor = new BABYLON.Color4(0.02, 0.02, 0.03, 1);
    sceneRef.current = scene;

    // Camera
    const camera = new BABYLON.ArcRotateCamera("camera", -Math.PI / 2, Math.PI / 3, 25, BABYLON.Vector3.Zero(), scene);
    camera.attachControl(canvasRef.current, true);
    // Editor rule: arrow keys must NEVER move props or the camera — props move only via
    // mouse-drag gizmos; pan/rotate via mouse drag; zoom via mouse wheel / pinch.
    try { camera.inputs.removeByType('ArcRotateCameraKeyboardMoveInput'); } catch { /* input not attached */ }
    camera.lowerRadiusLimit = 5;
    camera.upperRadiusLimit = 100;
    // ---- Touch gesture tuning (mobile-friendly) ----
    camera.pinchDeltaPercentage = 0.01;
    camera.wheelDeltaPercentage = 0.01;
    camera.useNaturalPinchZoom = true;
    camera.panningSensibility = 800;
    // Prevent iOS/Android from stealing pinch gestures for page zoom
    try {
      const c = canvasRef.current;
      c.style.touchAction = 'none';
      c.addEventListener('gesturestart', (e) => e.preventDefault(), { passive: false });
      c.addEventListener('gesturechange', (e) => e.preventDefault(), { passive: false });
    } catch { /* ignore */ }

    // Light
    const light = new BABYLON.HemisphericLight("ambient", new BABYLON.Vector3(0, 1, 0), scene);
    light.intensity = 0.7;

    // Grid
    const gridSize = 50;
    const grid = BABYLON.MeshBuilder.CreateGround("grid", { width: gridSize, height: gridSize }, scene);
    const gridMaterial = new GridMaterial("gridMat", scene);
    gridMaterial.majorUnitFrequency = 5;
    gridMaterial.minorUnitVisibility = 0.3;
    gridMaterial.gridRatio = 1;
    gridMaterial.backFaceCulling = false;
    gridMaterial.mainColor = new BABYLON.Color3(0.1, 0.1, 0.1);
    gridMaterial.lineColor = new BABYLON.Color3(0.03, 0.7, 0.83);
    gridMaterial.opacity = 0.5;
    grid.material = gridMaterial;
    grid.position.y = -0.01;

    // Create objects from game data
    createGameObjects(scene, project.game_data?.objects || []);

    // Initialize Gizmo Manager for transformation
    const gizmoManager = new BABYLON.GizmoManager(scene);
    // Instantiate ALL three gizmos up front — drag-end observers registered below would
    // silently no-op on null gizmos (the rotation/scale never-persisted bug).
    gizmoManager.positionGizmoEnabled = true;
    gizmoManager.rotationGizmoEnabled = true;
    gizmoManager.scaleGizmoEnabled = true;
    gizmoManager.boundingBoxGizmoEnabled = false;
    gizmoManager.usePointerToAttachGizmos = true;
    // Larger gizmos + increased hit-tolerance for finger dragging on mobile.
    const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    if (isTouchDevice) {
      const scale = 1.9;
      const applyScale = (g) => { if (g) g.scaleRatio = scale; };
      applyScale(gizmoManager.gizmos.positionGizmo);
      applyScale(gizmoManager.gizmos.rotationGizmo);
      applyScale(gizmoManager.gizmos.scaleGizmo);
    }
    gizmoManagerRef.current = gizmoManager;
    // Store scene mesh map so hierarchy → gizmo sync can look up meshes by object name.
    meshRegistryRef.current = new Map();
    (scene.meshes || []).forEach(m => meshRegistryRef.current.set(m.name, m));

    // Handle gizmo drag end to update position (group-aware)
    gizmoManager.gizmos.positionGizmo.onDragEndObservable.add(() => {
      const attachedMesh = gizmoManager.gizmos.positionGizmo?.attachedMesh;
      if (attachedMesh) {
        const pos = [
          parseFloat(attachedMesh.position.x.toFixed(2)),
          parseFloat(attachedMesh.position.y.toFixed(2)),
          parseFloat(attachedMesh.position.z.toFixed(2))
        ];
        applyGroupMove(attachedMesh.name, pos);
      }
    });

    gizmoManager.gizmos.rotationGizmo.onDragEndObservable.add(() => {
      const attachedMesh = gizmoManager.gizmos.rotationGizmo?.attachedMesh;
      if (attachedMesh) {
        const rot = [
          parseFloat(attachedMesh.rotation.x.toFixed(2)),
          parseFloat(attachedMesh.rotation.y.toFixed(2)),
          parseFloat(attachedMesh.rotation.z.toFixed(2))
        ];
        updateObjectProperty(attachedMesh.name, 'rotation', rot);
      }
    });

    gizmoManager.gizmos.scaleGizmo.onDragEndObservable.add(() => {
      const attachedMesh = gizmoManager.gizmos.scaleGizmo?.attachedMesh;
      if (attachedMesh) {
        const scale = [
          parseFloat(attachedMesh.scaling.x.toFixed(2)),
          parseFloat(attachedMesh.scaling.y.toFixed(2)),
          parseFloat(attachedMesh.scaling.z.toFixed(2))
        ];
        // scale_abs marks this as an absolute world-space scale — survives every rebuild.
        updateObjectProps(attachedMesh.name, { scale, scale_abs: true });
      }
    });

    // Default tool is Move — rotate/scale enable via the toolbar (instances stay alive).
    gizmoManager.rotationGizmoEnabled = false;
    gizmoManager.scaleGizmoEnabled = false;

    // Sync hierarchy panel selection when the user clicks a mesh directly in the scene.
    // POINTERTAP fires on EVERY click (POINTERPICK only fires when a mesh is hit),
    // so empty-canvas clicks reliably deselect and return the panel to Game Settings.
    scene.onPointerObservable.add((info) => {
      if (info.type !== BABYLON.PointerEventTypes.POINTERTAP) return;
      const picked = info.pickInfo?.hit ? info.pickInfo.pickedMesh : null;
      if (!picked || !picked.isPickable || picked.name.startsWith('__') || picked.name === 'grid' || picked.name === 'skyBox') {
        setSelectedObject(null);
        return;
      }
      // Highlight in hierarchy — this also opens the properties panel for the mesh.
      setSelectedObject(picked.name);
    });

    // Handle window resize
    // Debounce engine.resize so layout thrashes (mobile keyboard show/hide, sidebar toggles) don't
    // spam the pipeline and cause the camera to snap or the canvas to blink.
    let resizeTimer = null;
    const handleResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { try { engine.resize(); } catch (_) { /* engine disposed */ } }, 120);
    };
    window.addEventListener('resize', handleResize);

    // Render loop
    engine.runRenderLoop(() => scene.render());

    return () => {
      window.removeEventListener('resize', handleResize);
      engine.dispose();
      initializedRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projectReady]);

  // Only rebuild the whole scene when the *set of objects* changes (adds/removes/rename/type-change).
  // Individual property tweaks are handled by an incremental updater below, so we don't blink or
  // reset the camera every time the user edits a color or position.
  const objectsSignature = useMemo(() => {
    const objs = project?.game_data?.objects || [];
    return objs.map(o => `${o.name}:${o.type}:${o.asset_url || ''}`).join('|');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.game_data?.objects]);

  useEffect(() => {
    if (sceneRef.current && project?.game_data?.objects) {
      // OnDestroy purge: detach transform handles, then dispose render nodes + materials + textures
      const gm = gizmoManagerRef.current;
      if (gm) gm.attachToMesh(null);
      Object.values(meshMapRef.current).forEach(mesh => { try { mesh.dispose(false, true); } catch { /* already disposed */ } });
      meshMapRef.current = {};
      createGameObjects(sceneRef.current, project.game_data.objects);
      // Unlink/relink: re-attach the gizmo to the surviving selection or clear it
      if (selectedObject) {
        const survivor = meshMapRef.current[selectedObject];
        if (survivor && gm) gm.attachToMesh(survivor);
        else setSelectedObject(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectsSignature]);

  // Incremental update: whenever the objects list re-renders, sync position/scale/color/rotation
  // onto the existing meshes without disposing them (this stops the "blink").
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    (project?.game_data?.objects || []).forEach(obj => {
      const mesh = scene.getMeshByName(obj.name);
      if (!mesh) return;
      if (obj.position) {
        mesh.position.x = obj.position[0]; mesh.position.y = obj.position[1]; mesh.position.z = obj.position[2];
      }
      if (obj.rotation) {
        mesh.rotation.x = obj.rotation[0]; mesh.rotation.y = obj.rotation[1]; mesh.rotation.z = obj.rotation[2];
      }
      if (!['light', 'particle'].includes(obj.type)) {
        const es = effectiveScale(obj);
        mesh.scaling.x = es[0]; mesh.scaling.y = es[1]; mesh.scaling.z = es[2];
      }
      if (obj.color && mesh.material?.diffuseColor && !obj.asset_url) {
        try { mesh.material.diffuseColor = BABYLON.Color3.FromHexString(obj.color); } catch (_) { /* invalid hex */ }
      }
    });
  }, [project?.game_data?.objects]);

  const createGameObjects = (scene, objects) => {
    objects.forEach(obj => {
      let mesh;
      const position = obj.position || [0, 0, 0];
      const scale = obj.scale || [1, 1, 1];
      const color = obj.color || '#06B6D4';

      // Create mesh based on type
      switch (obj.type) {
        case 'player':
        case 'enemy':
        case 'npc':
        case 'collectible':
        case 'goal':
        case 'vehicle':
        case 'decoration': {
          // Actors are ALWAYS sprites in the editor — never capsules/boxes/spheres.
          // Unit-size geometry; final size comes from effectiveScale() applied below.
          mesh = BABYLON.MeshBuilder.CreatePlane(obj.name, { size: 1, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
          if (!obj.asset_url && !(obj.textures && obj.textures.diffuse)) {
            mesh.metadata = { ...(mesh.metadata || {}), proceduralRole: obj.type };
          }
          break;
        }
        case 'platform':
        case 'floor':
        case 'wall':
        case 'structure':
        case 'puzzle_piece':
          mesh = BABYLON.MeshBuilder.CreateBox(obj.name, { size: 1 }, scene);
          break;
        case 'terrain':
          mesh = BABYLON.MeshBuilder.CreateGround(obj.name, { width: 1, height: 1 }, scene);
          break;
        // Explicit user-selected primitives — the ONLY path that creates raw geometric shapes
        case 'cube':
          mesh = BABYLON.MeshBuilder.CreateBox(obj.name, { size: 1 }, scene);
          break;
        case 'cylinder':
          mesh = BABYLON.MeshBuilder.CreateCylinder(obj.name, { height: 1, diameter: 1 }, scene);
          break;
        case 'sphere':
          mesh = BABYLON.MeshBuilder.CreateSphere(obj.name, { diameter: 1 }, scene);
          break;
        case 'cone':
          mesh = BABYLON.MeshBuilder.CreateCylinder(obj.name, { height: 1, diameterTop: 0, diameterBottom: 1 }, scene);
          break;
        case 'torus':
        case 'ring':
          mesh = BABYLON.MeshBuilder.CreateTorus(obj.name, { diameter: 1, thickness: 0.3 }, scene);
          break;
        case 'capsule':
          mesh = BABYLON.MeshBuilder.CreateCapsule(obj.name, { height: 1, radius: 0.25 }, scene);
          break;
        case 'plane':
          mesh = BABYLON.MeshBuilder.CreatePlane(obj.name, { size: 1, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
          break;
        case 'sprite':
          // Textured billboard plane — real asset visual, not a solid block
          mesh = BABYLON.MeshBuilder.CreatePlane(obj.name, { size: 1, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
          // Upright by default (rotation.x = 0) so sprites don't come in inverted.
          // Only enable billboard when the object explicitly asks for it — otherwise the user
          // can rotate it freely and see the change in the scene.
          if (obj.billboard !== false && obj.billboard === true) {
            mesh.billboardMode = BABYLON.Mesh.BILLBOARDMODE_Y;
          }
          break;
        case 'texture_prop':
          // Textured box (uses the asset URL as diffuse texture on every face)
          mesh = BABYLON.MeshBuilder.CreateBox(obj.name, { size: 1 }, scene);
          break;
        case 'particle': {
          // Visible emitter marker + a live ParticleSystem so designers preview effects in the editor too.
          mesh = BABYLON.MeshBuilder.CreateSphere(obj.name, { diameter: 0.4 }, scene);
          try {
            const ps = new BABYLON.ParticleSystem(obj.name + '_ps', obj.particles?.capacity || 300, scene);
            ps.particleTexture = new BABYLON.Texture(obj.asset_url || 'https://assets.babylonjs.com/textures/flare.png', scene);
            ps.emitter = mesh;
            ps.minEmitBox = new BABYLON.Vector3(-0.1, 0, -0.1);
            ps.maxEmitBox = new BABYLON.Vector3(0.1, 0, 0.1);
            ps.color1 = BABYLON.Color4.FromHexString((obj.particles?.color1 || (obj.color || '#06B6D4')) + (obj.particles?.color1?.length === 9 ? '' : 'FF'));
            ps.color2 = BABYLON.Color4.FromHexString((obj.particles?.color2 || '#FFFFFF') + (obj.particles?.color2?.length === 9 ? '' : 'FF'));
            ps.colorDead = new BABYLON.Color4(0, 0, 0, 0);
            ps.minSize = obj.particles?.min_size || 0.1;
            ps.maxSize = obj.particles?.max_size || 0.5;
            ps.minLifeTime = obj.particles?.min_lifetime || 0.3;
            ps.maxLifeTime = obj.particles?.max_lifetime || 1.5;
            ps.emitRate = obj.particles?.emit_rate || 60;
            ps.direction1 = new BABYLON.Vector3(-1, 2, -1);
            ps.direction2 = new BABYLON.Vector3(1, 4, 1);
            ps.gravity = new BABYLON.Vector3(0, -3, 0);
            ps.blendMode = BABYLON.ParticleSystem.BLENDMODE_ONEONE;
            ps.start();
          } catch { /* keep marker mesh */ }
          break;
        }
        case 'background':
          // A wide plane locked behind everything else with parallax metadata.
          mesh = BABYLON.MeshBuilder.CreatePlane(obj.name, { width: 1, height: 1, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
          mesh.isPickable = true;
          mesh.renderingGroupId = 0;
          break;
        case 'model_3d':
          // Placeholder box while the .glb loads asynchronously — actual mesh is loaded in dropAssetOnScene.
          // On reload, we lazy-load via SceneLoader below.
          if (obj.asset_url && /\.(glb|gltf)(\?|$)/i.test(obj.asset_url)) {
            try {
              BABYLON.SceneLoader.ImportMesh('', '', obj.asset_url, scene, (loaded) => {
                if (loaded[0]) {
                  loaded[0].name = obj.name;
                  loaded[0].position = new BABYLON.Vector3(position[0], position[1], position[2]);
                  loaded[0].isPickable = true;
                  meshMapRef.current[obj.name] = loaded[0];
                }
              });
            } catch { /* keep placeholder */ }
          }
          mesh = BABYLON.MeshBuilder.CreateBox(obj.name, { size: 1 }, scene);
          break;
        case 'light':
          // Create point light with visual indicator
          const pointLight = new BABYLON.PointLight(obj.name, new BABYLON.Vector3(...position), scene);
          pointLight.intensity = obj.intensity || 1;
          mesh = BABYLON.MeshBuilder.CreateSphere(obj.name + "_indicator", { diameter: 0.3 }, scene);
          const emissiveMat = new BABYLON.StandardMaterial(obj.name + "_mat", scene);
          emissiveMat.emissiveColor = new BABYLON.Color3(1, 0.9, 0.5);
          mesh.material = emissiveMat;
          break;
        default:
          // No unsolicited cubes: unknown roles render as a flat, selectable marker plane
          // (texture applies with alpha if the entity has art; still editable/deletable).
          mesh = BABYLON.MeshBuilder.CreatePlane(obj.name, { size: 1, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
      }

      if (mesh && obj.type !== 'light') {
        // Position + rotation + unified scale (geometry is unit-size; scale is world-space)
        mesh.position = new BABYLON.Vector3(position[0], position[1], position[2]);
        if (obj.rotation) mesh.rotation = new BABYLON.Vector3(obj.rotation[0] || 0, obj.rotation[1] || 0, obj.rotation[2] || 0);
        if (obj.type !== 'particle') {
          const es = effectiveScale(obj);
          mesh.scaling = new BABYLON.Vector3(es[0], es[1], es[2]);
        }

        // Material — full PBR/standard stack with all texture maps (diffuse/normal/ao/emissive/metallic/roughness),
        // UV tiling and alpha modes. See /app/frontend/src/lib/material.js.
        const material = buildObjectMaterial(BABYLON, scene, obj, color);
        mesh.material = material;
        // Art-less actors get drawn role sprites — never flat colored primitives
        if (mesh.metadata?.proceduralRole) {
          const ptx = new BABYLON.Texture(roleSpriteDataUrl(mesh.metadata.proceduralRole, color), scene, false, true);
          ptx.hasAlpha = true;
          material.diffuseTexture = ptx;
          material.useAlphaFromDiffuseTexture = true;
          material.backFaceCulling = false;
        }

        // Make mesh pickable
        mesh.isPickable = true;
        mesh.actionManager = new BABYLON.ActionManager(scene);
        mesh.actionManager.registerAction(
          new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPickTrigger, () => {
            setSelectedObject(obj.name);
          })
        );
      }

      if (mesh) {
        meshMapRef.current[obj.name] = mesh;
      }
    });
  };

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}`, { withCredentials: true });
      setProject(res.data);
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchCopilotHistory = async () => {
    try {
      const res = await axios.get(`${API}/copilot/history/${projectId}`, { withCredentials: true });
      setCopilotMessages(res.data || []);
    } catch (error) {
      console.error('Failed to load copilot history');
    }
  };

  const saveProject = async (arg) => {
    const quiet = arg === true; // autosave path — no success toasts, no UI noise
    setSaving(true);
    try {
      // Auto-snapshot BEFORE overwriting so the user can "revert to saved"
      try { await axios.post(`${API}/projects/${projectId}/checkpoint`, {}, { withCredentials: true }); } catch { /* ignore */ }

      // Compile any AI-authored behavior scripts before saving so runtime doesn't need to call the LLM.
      let gd = gameDataRef.current || project.game_data || {};
      const needsCompile = (gd.objects || []).some(o => (o.scripts || []).some(s => s.body && !s.compiled));
      if (needsCompile) {
        try {
          const pending = (gd.objects || [])
            .map(o => ({ name: o.name, type: o.type, scripts: (o.scripts || []).filter(s => s.body && !s.compiled) }))
            .filter(o => o.scripts.length);
          if (pending.length) {
            const r = await axios.post(`${API}/copilot/compile_scripts`,
              { project_id: projectId, objects: pending },
              { withCredentials: true, timeout: 12000 }); // hard cap — a down LLM must NEVER stall Save
            const compiledMap = new Map((r.data?.objects || []).map(o => [o.name, o.scripts]));
            gd = {
              ...gd,
              objects: (gd.objects || []).map(o => {
                if (!compiledMap.has(o.name)) return o;
                const compiled = compiledMap.get(o.name);
                return {
                  ...o,
                  scripts: (o.scripts || []).map(s => {
                    const found = compiled.find(cs => cs.name === s.name);
                    return found ? { ...s, ...found } : s;
                  })
                };
              })
            };
            setProject(prev => prev ? { ...prev, game_data: gd } : prev);
            toast.success('Behaviors compiled to runnable code');
          }
        } catch { /* non-blocking — save even if compile fails */ }
      }

      // Multi-level normalization: the canvas buffer holds the CURRENTLY-edited level's
      // objects. Persist it into its stage slot; game_data.objects must stay = Level 1
      // (the runtime's contract: stage 0 plays from game_data.objects).
      let saveGd = gd;
      if (gd.projectDefinition?.stages?.length) {
        const curStage = editingStageRef.current;
        const stages = [...gd.projectDefinition.stages];
        stages[curStage] = { ...(stages[curStage] || {}), objects: gd.objects || [] };
        saveGd = {
          ...gd,
          projectDefinition: { ...gd.projectDefinition, stages },
          objects: (curStage === 0 ? gd.objects : stages[0]?.objects) || gd.objects || [],
        };
      }

      let saveOk = false;
      for (let attempt = 0; attempt < 3 && !saveOk; attempt++) {
        try {
          await axios.put(`${API}/projects/${projectId}`, { game_data: saveGd }, { withCredentials: true, timeout: 30000 });
          saveOk = true;
        } catch (err) {
          // Transient network/proxy blips self-heal: retry twice with backoff.
          if (attempt === 2 || [401, 403, 413].includes(err.response?.status)) throw err;
          await new Promise(res => setTimeout(res, 1200 * (attempt + 1)));
        }
      }
      if (!quiet) toast.success('Project saved!');
      // Live playtest hot-reload: a save instantly restarts the mini playtest with the new state.
      if (playtestOpen) setPlaytestKey(k => k + 1);
      return true;
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      toast.error(
        status === 413 ? 'Save failed: project too large — remove some big imported images'
        : status === 401 || status === 403 ? 'Save failed: session expired — please log in again'
        : `Save failed${status ? ` (${status})` : ''}${detail ? `: ${detail}` : ' — check your connection and retry'}`
      );
      return false;
    } finally {
      setSaving(false);
    }
  };
  saveFnRef.current = saveProject;

  const revertToLastCheckpoint = async () => {
    if (!window.confirm('Revert this project to the last saved checkpoint?\n\nThis WILL discard any unsaved changes you have made since the last save.\n\nA pre-revert checkpoint is created automatically, so you can undo the revert.\n\nContinue?')) return;
    try {
      const list = await axios.get(`${API}/projects/${projectId}/checkpoints`, { withCredentials: true });
      const chks = list.data?.checkpoints || [];
      if (!chks.length) { toast.info('No checkpoints yet — save the project first.'); return; }
      await axios.post(`${API}/projects/${projectId}/revert`,
        { checkpoint_id: chks[0].checkpoint_id, confirm: true },
        { withCredentials: true }
      );
      toast.success('Reverted to last save — reloading…');
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revert failed');
    }
  };

  // In-editor undo stack — snapshots the game_data before every user mutation
  const undoStackRef = useRef([]);
  // Live snapshot of the latest game_data — keyboard handlers hold stale closures of
  // `project`, which made undo restore the OPENING state and Ctrl+S save old versions
  // (silently wiping all work). All undo/redo/save paths read THIS ref instead.
  const gameDataRef = useRef(null);
  useEffect(() => { gameDataRef.current = project?.game_data || null; }, [project]);
  const redoStackRef = useRef([]);
  const pushUndo = () => {
    if (!gameDataRef.current) return;
    try {
      // Snapshot includes the edited level index — undoing across a level switch must
      // restore BOTH the canvas buffer and the level selector, or Save writes the
      // buffer into the wrong stage slot (cross-level data loss).
      const snap = JSON.stringify({ __v: 2, stage: editingStageRef.current, gd: gameDataRef.current });
      // Dedupe: many mutation paths call pushUndo twice for one user action — identical
      // consecutive snapshots made Ctrl+Z appear dead (it "undid" to the same state).
      if (undoStackRef.current[undoStackRef.current.length - 1] === snap) return;
      undoStackRef.current.push(snap);
      if (undoStackRef.current.length > 30) undoStackRef.current.shift();
      // Any new user change invalidates the redo history
      redoStackRef.current = [];
    } catch { /* ignore */ }
  };
  const parseSnap = (snap) => {
    const p = JSON.parse(snap);
    return p && p.__v === 2 ? { gd: p.gd, stage: p.stage } : { gd: p, stage: undefined };
  };
  const undoLast = () => {
    const snap = undoStackRef.current.pop();
    if (!snap) { toast.info('Nothing to undo'); return; }
    try {
      const { gd, stage } = parseSnap(snap);
      // Save current state to redo stack so we can go back
      if (gameDataRef.current) redoStackRef.current.push(JSON.stringify({ __v: 2, stage: editingStageRef.current, gd: gameDataRef.current }));
      setProject(prev => prev ? { ...prev, game_data: gd } : prev);
      if (stage !== undefined) setEditingStage(stage);
      toast.success('Undid last change');
    } catch { toast.error('Undo failed'); }
  };
  const redoLast = () => {
    const snap = redoStackRef.current.pop();
    if (!snap) { toast.info('Nothing to redo'); return; }
    try {
      const { gd, stage } = parseSnap(snap);
      if (gameDataRef.current) undoStackRef.current.push(JSON.stringify({ __v: 2, stage: editingStageRef.current, gd: gameDataRef.current }));
      setProject(prev => prev ? { ...prev, game_data: gd } : prev);
      if (stage !== undefined) setEditingStage(stage);
      toast.success('Redid last change');
    } catch { toast.error('Redo failed'); }
  };

  // Selecting from the hierarchy attaches the active gizmo to the matching mesh so
  // the object can be moved/rotated/scaled with visible handles in the 3D canvas.
  useEffect(() => {
    const gm = gizmoManagerRef.current;
    const scene = sceneRef.current;
    if (!gm || !scene) return;
    if (!selectedObject) { gm.attachToMesh(null); return; }
    const mesh = scene.getMeshByName(selectedObject);
    if (mesh) gm.attachToMesh(mesh);
  }, [selectedObject]);


  // Keyboard shortcuts: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y (redo), Delete (remove selected)
  useEffect(() => {
    const isTypingField = (el) => {
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || el.isContentEditable;
    };
    const onKey = (e) => {
      if (isTypingField(document.activeElement)) return;
      const meta = e.ctrlKey || e.metaKey;
      if (meta && (e.key === 'z' || e.key === 'Z') && !e.shiftKey) {
        e.preventDefault(); undoLast();
      } else if (meta && ((e.key === 'z' || e.key === 'Z') && e.shiftKey || e.key === 'y' || e.key === 'Y')) {
        e.preventDefault(); redoLast();
      } else if (e.key === 'Escape') {
        // Deselect — returns the right panel to Game Settings
        setSelectedObject(null);
        setMultiSel([]);
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedObject) {
        e.preventDefault();
        pushUndo();
        setProject(prev => ({
          ...prev,
          game_data: {
            ...prev.game_data,
            objects: (prev.game_data?.objects || []).filter(o => o.name !== selectedObject)
          }
        }));
        setSelectedObject(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObject, project]);

  // Gizmo mode switching
  const switchGizmoMode = (mode) => {
    setGizmoMode(mode);
    if (gizmoManagerRef.current) {
      gizmoManagerRef.current.positionGizmoEnabled = mode === 'position';
      gizmoManagerRef.current.rotationGizmoEnabled = mode === 'rotation';
      gizmoManagerRef.current.scaleGizmoEnabled = mode === 'scale';
      // Re-apply touch-friendly scale in case a gizmo was just created
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      if (isTouch) {
        const s = 1.9;
        const g = gizmoManagerRef.current.gizmos;
        if (g?.positionGizmo) g.positionGizmo.scaleRatio = s;
        if (g?.rotationGizmo) g.rotationGizmo.scaleRatio = s;
        if (g?.scaleGizmo) g.scaleGizmo.scaleRatio = s;
      }
    }
  };

  // Publish/Unpublish game
  const togglePublish = async () => {
    setPublishing(true);
    try {
      if (project.published) {
        await axios.post(`${API}/projects/${projectId}/unpublish`, {}, { withCredentials: true });
        setProject(prev => ({ ...prev, published: false }));
        toast.success('Game unpublished');
      } else {
        // Save first, then publish
        await axios.put(`${API}/projects/${projectId}`, {
          game_data: project.game_data
        }, { withCredentials: true });
        await axios.post(`${API}/projects/${projectId}/publish`, {}, { withCredentials: true });
        setProject(prev => ({ ...prev, published: true }));
        toast.success('Game published to Community Gallery!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish', { duration: 6000 });
    } finally {
      setPublishing(false);
    }
  };

  const sendCopilotMessage = async () => {
    if (!copilotInput.trim()) return;
    
    const userMsg = { role: 'user', content: copilotInput };
    setCopilotMessages(prev => [...prev, userMsg]);
    setCopilotInput('');
    setCopilotLoading(true);

    try {
      const res = await axios.post(`${API}/copilot/chat`, {
        message: copilotInput,
        project_id: projectId
      }, { withCredentials: true });

      const aiMsg = { 
        role: 'assistant', 
        content: res.data.response,
        action: res.data.action
      };
      setCopilotMessages(prev => [...prev, aiMsg]);

      // Update game data if action was taken
      if (res.data.game_data) {
        setProject(prev => ({ ...prev, game_data: res.data.game_data }));
      }
    } catch (error) {
      toast.error('Copilot error');
    } finally {
      setCopilotLoading(false);
    }
  };

  const handleCopilotKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendCopilotMessage();
    }
  };

  const updateObjectProps = (objName, props) => {
    pushUndo();
    // Group semantics: an attribute applied while multi-selecting (or to a grouped
    // object) lands on EVERY member. Properties are stored per object, so they
    // persist individually after ungrouping (removable manually or via undo).
    setProject(prev => {
      const objs = prev.game_data?.objects || [];
      const target = objs.find(o => o.name === objName);
      const names = new Set([objName]);
      if (multiSel.length > 1 && multiSel.includes(objName)) multiSel.forEach(n => names.add(n));
      else if (target?.group && !('position' in props)) objs.forEach(o => { if (o.group === target.group) names.add(o.name); });
      const objects = objs.map(o => names.has(o.name) ? { ...o, ...props } : o);
      return { ...prev, game_data: { ...prev.game_data, objects } };
    });
  };
  const updateObjectProperty = (objName, property, value) => updateObjectProps(objName, { [property]: value });

  // Group-aware move: dragging any member of a group moves the whole group by the same delta.
  const applyGroupMove = (objName, newPos) => {
    const objs = gameDataRef.current?.objects || [];
    const target = objs.find(o => o.name === objName);
    const group = target?.group;
    if (!group) { updateObjectProperty(objName, 'position', newPos); return; }
    const old = target.position || [0, 0, 0];
    const delta = [newPos[0] - old[0], newPos[1] - old[1], newPos[2] - old[2]];
    pushUndo();
    setProject(prev => ({
      ...prev,
      game_data: {
        ...prev.game_data,
        objects: prev.game_data.objects.map(o => {
          if (o.name === objName) return { ...o, position: newPos };
          if (o.group === group) {
            const p = o.position || [0, 0, 0];
            return { ...o, position: [+(p[0] + delta[0]).toFixed(2), +(p[1] + delta[1]).toFixed(2), +(p[2] + delta[2]).toFixed(2)] };
          }
          return o;
        }),
      },
    }));
  };

  // Apply/remove a real surface texture on an entity (tiled or flat — like any game engine)
  const applySurfaceTexture = (objName, url) => {
    pushUndo();
    setProject(prev => ({
      ...prev,
      game_data: {
        ...prev.game_data,
        objects: prev.game_data.objects.map(o => {
          if (o.name !== objName) return o;
          if (!url) {
            const { surface_mode, texture_mode, ...rest } = o;
            return { ...rest, textures: undefined };
          }
          return { ...o, asset_url: url, textures: { ...(o.textures || {}), diffuse: url }, surface_mode: 'texture', texture_mode: o.texture_mode || 'tiled', uv: o.uv || { uScale: 4, vScale: 4 } };
        }),
      },
    }));
  };

  const getSelectedObjectData = () => {
    if (!selectedObject || !project?.game_data?.objects) return null;
    return project.game_data.objects.find(o => o.name === selectedObject);
  };

  const commitRename = () => {
    const oldName = renamingObj;
    setRenamingObj(null);
    const newName = (renameVal || '').trim();
    if (!oldName || !newName || newName === oldName) return;
    if ((project?.game_data?.objects || []).some(o => o.name === newName)) {
      toast.error('An object with that name already exists');
      return;
    }
    pushUndo();
    setProject(prev => ({
      ...prev,
      game_data: {
        ...prev.game_data,
        objects: prev.game_data.objects.map(o => o.name === oldName ? { ...o, name: newName } : o)
      }
    }));
    if (selectedObject === oldName) setSelectedObject(newName);
    toast.success(`Renamed to "${newName}"`);
  };

  const groupSelected = () => {
    if (multiSel.length < 2) return;
    // No blocking prompt — auto-name the group; double-click the group header to rename it.
    const existing = new Set((project?.game_data?.objects || []).map(o => o.group).filter(Boolean));
    let n = existing.size + 1;
    let g = `Group_${n}`;
    while (existing.has(g)) { n += 1; g = `Group_${n}`; }
    pushUndo();
    setProject(prev => ({
      ...prev,
      game_data: {
        ...prev.game_data,
        objects: prev.game_data.objects.map(o => multiSel.includes(o.name) ? { ...o, group: g } : o)
      }
    }));
    toast.success(`Grouped ${multiSel.length} objects into "${g}" — moving one moves all. Double-click the group to rename.`);
    setMultiSel([]);
  };

  const renameGroup = (oldName, newName) => {
    const g = (newName || '').trim();
    if (!g || g === oldName) return;
    pushUndo();
    setProject(prev => ({
      ...prev,
      game_data: {
        ...prev.game_data,
        objects: prev.game_data.objects.map(o => o.group === oldName ? { ...o, group: g } : o)
      }
    }));
    setCollapsedGroups(prev => { const { [oldName]: was, ...rest } = prev; return { ...rest, [g]: was }; });
    toast.success(`Group renamed to "${g}"`);
  };

  const duplicateGroup = (gname) => {
    const members = (project?.game_data?.objects || []).filter(o => o.group === gname);
    if (!members.length) return;
    pushUndo();
    const suffix = Date.now().toString(36).slice(-4);
    const newGroup = `${gname}_copy_${suffix}`;
    const clones = members.map(o => ({
      ...JSON.parse(JSON.stringify(o)),
      name: `${o.name}_copy_${suffix}`,
      group: newGroup,
      position: [(o.position?.[0] || 0) + 2, o.position?.[1] || 0, (o.position?.[2] || 0) + 2],
    }));
    setProject(prev => ({
      ...prev,
      game_data: { ...prev.game_data, objects: [...prev.game_data.objects, ...clones] }
    }));
    toast.success(`Duplicated "${gname}" → "${newGroup}" (${clones.length} objects)`);
  };

  const ungroup = (gname) => {
    pushUndo();
    setProject(prev => ({
      ...prev,
      game_data: {
        ...prev.game_data,
        objects: prev.game_data.objects.map(o => o.group === gname ? { ...o, group: undefined } : o)
      }
    }));
    toast.success(`Ungrouped "${gname}"`);
  };

  const addObjectOfType = (type) => {
    pushUndo();
    const spawnX = ((project?.game_data?.objects?.length || 0) % 9) - 4; // spread new objects, never stack on the hero
    const newObj = {
      type,
      name: `${type.charAt(0).toUpperCase() + type.slice(1)}_${Date.now().toString(36).slice(-4)}`,
      position: [spawnX, 1, 0],
      scale: type === 'sprite' ? [2.5, 2.5, 2.5] : type === 'platform' ? [2, 0.5, 2] : [1, 1, 1],
      color: type === 'enemy' ? '#EF4444' : type === 'collectible' ? '#FBBF24' : '#64748B',
      ...(type === 'particle' ? { particles: { emit_rate: 60, capacity: 300, min_size: 0.1, max_size: 0.5, color1: '#06B6D4', color2: '#FFFFFF' } } : {}),
    };
    setProject(prev => ({
      ...prev,
      game_data: {
        ...prev.game_data,
        objects: [...(prev.game_data?.objects || []), newObj]
      }
    }));
    setSelectedObject(newObj.name);
    toast.success(`Added ${type} "${newObj.name}"`);
  };

  // ---- Drag & drop assets from AssetsPanel onto the Babylon canvas ----
  /**
   * Classify a dropped asset into: sprite | texture | background | model_3d.
   * Priority order:
   *   1. Explicit `asset_type` on the payload (from the AssetsPanel/Library).
   *   2. File extension (.glb/.gltf/.fbx/.obj → 3d model).
   *   3. Detect via ping to load: if the image aspect ratio > 1.9 → background (parallax-eligible).
   *   4. Otherwise fall back to sprite (billboard).
   */
  const classifyDroppedAsset = useCallback(async (payload) => {
    const explicit = (payload.asset_type || '').toLowerCase();
    // 'auto' means the upstream tagger doesn't know — always sniff.
    if (explicit && explicit !== 'auto' && ['sprite', 'texture', 'background', 'model_3d', 'texture_prop'].includes(explicit)) {
      return explicit === 'texture_prop' ? 'texture' : explicit;
    }
    const url = (payload.url || '').toLowerCase();
    if (/\.(glb|gltf|fbx|obj)(\?|$)/.test(url)) return 'model_3d';
    // Sniff dimensions
    try {
      const dims = await new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
        img.onerror = () => resolve({ w: 1, h: 1 });
        img.src = payload.url;
      });
      const ratio = dims.w / dims.h;
      if (ratio >= 1.9) return 'background';
    } catch { /* fall through */ }
    return 'sprite';
  }, []);

  const dropAssetOnScene = useCallback(async (payload, evt) => {
    pushUndo();
    const scene = sceneRef.current;
    if (!scene || !payload?.url) return;
    let dropPos;
    try {
      const pickInfo = scene.pick(evt.offsetX ?? 0, evt.offsetY ?? 0, (m) => m.isPickable && m.name !== 'skyBox');
      if (pickInfo?.hit && pickInfo.pickedPoint) {
        dropPos = pickInfo.pickedPoint.clone();
        dropPos.y += 1;
      } else {
        const cam = scene.activeCamera;
        dropPos = cam.position.add(cam.getForwardRay().direction.scale(8));
      }
    } catch {
      dropPos = new BABYLON.Vector3(0, 2, 0);
    }

    const kind = await classifyDroppedAsset(payload);
    const size = 2.5;
    const safeName = (payload.name || 'asset').replace(/\s+/g, '_').slice(0, 24);
    const name = `Asset_${safeName}_${Date.now().toString(36)}`;
    let mesh;
    let objType = 'sprite';
    let extraProps = {};

    if (kind === 'model_3d') {
      // Load a GLB/GLTF at the drop position via BABYLON.SceneLoader.
      try {
        const { rootMesh } = await new Promise((resolve, reject) => {
          BABYLON.SceneLoader.ImportMesh('', '', payload.url, scene,
            (loaded) => resolve({ rootMesh: loaded[0] }),
            null,
            (_, msg, exc) => reject(exc || new Error(msg)));
        });
        rootMesh.name = name;
        rootMesh.position = dropPos;
        rootMesh.isPickable = true;
        mesh = rootMesh;
        objType = 'model_3d';
      } catch (err) {
        toast.error(`3D model load failed: ${err.message || 'unknown'}`);
        return;
      }
    } else if (kind === 'texture') {
      // Textures are resources, not scene entities. Register on the project's textures[] list.
      const nextTex = [ ...(project?.game_data?.textures || []), { asset_id: payload.asset_id, url: payload.url, name: payload.name } ];
      setProject(prev => prev ? { ...prev, game_data: { ...prev.game_data, textures: nextTex } } : prev);
      toast.success(`Added "${payload.name}" to material library (Textures)`);
      return;
    } else if (kind === 'background') {
      mesh = BABYLON.MeshBuilder.CreatePlane(name, { width: size * 4, height: size * 2, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
      mesh.position = dropPos;
      mesh.position.z += 10; // push behind
      objType = 'background';
      extraProps = { parallax: true, locked_to_camera: true, non_interactive: true, draw_order: -1000 };
    } else {
      // Auto-remove solid backgrounds from JPG-style sprites before texturing.
      const cleanedUrl = await removeBackgroundIfNeeded(payload.url);
      mesh = BABYLON.MeshBuilder.CreatePlane(name, { size, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
      mesh.position = dropPos;
      // Do NOT force billboardMode by default — that hides user rotation. Users can opt in via properties.
      objType = 'sprite';
      // Force upright orientation so PNG/JPG sprites don't come in flipped or inverted.
      mesh.rotation.set(0, 0, 0);
      extraProps = {
        collider: 'box',
        physics: { mass: 1, gravity_scale: 1, freeze_rotation: false },
        material: { alpha_mode: 'blend' },
        asset_url: cleanedUrl,
        textures: { diffuse: cleanedUrl },
        rotation: [0, 0, 0],
      };
      // Override the drop-time asset_url with the cleaned data URL for the material builder below.
      payload = { ...payload, url: cleanedUrl };
    }

    // Apply diffuse texture (with alpha) using the shared material helper for consistency.
    if (mesh && kind !== 'model_3d') {
      const stubObj = { name, asset_url: payload.url, textures: { diffuse: payload.url }, material: { alpha_mode: 'auto' } };
      mesh.material = buildObjectMaterial(BABYLON, scene, stubObj, '#FFFFFF');
      mesh.isPickable = true;
    }

    // Register in the mesh map so rebuilds/deletes fully own this mesh — otherwise the
    // drop-created mesh survives as an undeletable phantom decoration (the #1 sprite bug).
    if (mesh) meshMapRef.current[name] = mesh;

    const ROLE_TYPE = { player: 'player', hero: 'player', enemy: 'enemy', villain: 'enemy', collectible: 'collectible', pickup: 'collectible', goal: 'goal', platform: 'platform', background: 'background', decoration: 'decoration' };
    let castRole = (payload.role || '').toLowerCase();
    if (!castRole && objType === 'sprite') {
      // Cast on drop: a sprite immediately assumes a live gameplay role
      const pick = window.prompt(
        'Cast this sprite — what role should it play?\nhero, enemy, collectible, goal, background, decoration',
        'hero'
      );
      castRole = (pick || 'decoration').trim().toLowerCase();
      if (!ROLE_TYPE[castRole]) castRole = 'decoration';
    }
    const castType = ROLE_TYPE[castRole];
    // One-hero rule: casting a hero re-skins the existing player instead of spawning a second
    if (castType === 'player') {
      const existingPlayer = (project?.game_data?.objects || []).find(o => o.type === 'player');
      if (existingPlayer) {
        setProject(prev => prev ? ({
          ...prev,
          game_data: {
            ...prev.game_data,
            objects: prev.game_data.objects.map(o => o.type === 'player' ? { ...o, asset_url: payload.url, asset_id: payload.asset_id } : o),
          },
        }) : prev);
        toast.success(`"${payload.name}" is now the hero`);
        return;
      }
    }
    const newObj = {
      name,
      type: castType || objType,
      role: castRole || payload.role || undefined,
      position: [dropPos.x, dropPos.y, dropPos.z],
      color: castType === 'enemy' ? '#EF4444' : '#FFFFFF',
      asset_url: payload.url,
      asset_id: payload.asset_id,
      ...(castType === 'enemy' ? { behavior: 'patrol' } : {}),
      ...extraProps,
    };
    setProject(prev => prev ? ({
      ...prev,
      game_data: { ...prev.game_data, objects: [...(prev.game_data?.objects || []), newObj] },
    }) : prev);
    toast.success(`Dropped "${payload.name}" as ${castType ? castRole : objType} — it plays that role live`);
  }, [project, classifyDroppedAsset]);

  const onCanvasDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; };
  const onCanvasDrop = (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('application/x-playmold-asset');
    if (!raw) return;
    try {
      const payload = JSON.parse(raw);
      const rect = canvasRef.current?.getBoundingClientRect();
      const offsetX = rect ? e.clientX - rect.left : 0;
      const offsetY = rect ? e.clientY - rect.top : 0;
      dropAssetOnScene(payload, { offsetX, offsetY });
    } catch { /* ignore */ }
  };

  const selectedObjData = getSelectedObjectData();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#06B6D4]" />
      </div>
    );
  }

  return (
    <div className="h-[100dvh] bg-[#050505] flex flex-col overflow-hidden" data-testid="editor">
      <TouchCoachMark location="editor" />
      <EditorTour />
      {/* Header */}
      <header className="min-h-12 border-b border-[#1E1E24] bg-[#0A0A0B] flex items-center justify-between px-2 md:px-4 shrink-0 gap-2 flex-wrap py-1">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="hidden md:flex text-zinc-400 hover:text-white" data-testid="editor-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white w-8 h-8" title="Back" data-testid="editor-back-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="text-zinc-400 hover:text-white w-8 h-8" title="Home" data-testid="editor-home-btn">
            <Home className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Gamepad2 className="w-4 h-4 text-[#06B6D4]" />
            <span className="font-bold text-white truncate max-w-[38vw] md:max-w-none">{project?.name}</span>
            <span className="hidden sm:inline text-xs font-mono text-zinc-500 uppercase">{project?.genre}</span>
          </div>
          <p className="w-full text-[9px] text-zinc-500 md:hidden">Touch tips: tap to select · drag gizmo to move · pinch to zoom.</p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="bg-transparent border-[#1E1E24] hover:bg-white/5" onClick={undoLast} data-testid="editor-undo" title="Undo last change (Ctrl+Z)">
            ↶ Undo
          </Button>
          <Button variant="outline" size="sm" className="bg-transparent border-[#1E1E24] hover:bg-white/5" onClick={redoLast} data-testid="editor-redo" title="Redo (Ctrl+Y)">
            ↷ Redo
          </Button>
          <Button variant="outline" size="sm" className="bg-transparent border-[#1E1E24] hover:bg-white/5" onClick={revertToLastCheckpoint} data-testid="editor-revert" title="Revert to last saved checkpoint">
            ⟲ Revert
          </Button>
          <Button variant="outline" size="sm" className="bg-transparent border-[#1E1E24] hover:bg-white/5" onClick={async () => {
            // Pre-open the tab synchronously (user gesture) so popup blockers don't eat it.
            const w = window.open('about:blank', '_blank');
            toast.info('Saving your changes before preview…');
            const ok = await saveProject();
            if (!ok) {
              // Never preview stale state — surface the save failure instead.
              if (w) w.close();
              toast.error('Preview cancelled — your changes could not be saved');
              return;
            }
            if (w) w.location = `/play/${projectId}`;
            else navigate(`/play/${projectId}`);
          }} data-testid="editor-preview-game" title="Saves your changes, then plays the whole game">
            ▶ Preview
          </Button>
          <Button variant="outline" size="sm" className={`bg-transparent border-[#1E1E24] hover:bg-white/5 ${playtestOpen ? 'text-[#06B6D4] border-[#06B6D4]/50' : ''}`} onClick={async () => {
            if (playtestOpen) { setPlaytestOpen(false); return; }
            await saveProject();
            setPlaytestKey(k => k + 1);
            setPlaytestOpen(true);
          }} data-testid="editor-playtest-btn" title="Save, then playtest in a mini window without leaving the editor">
            ⧉ Playtest
          </Button>
          <Button variant="outline" size="sm" className="bg-transparent border-[#1E1E24] hover:bg-white/5" onClick={saveProject} disabled={saving} data-testid="editor-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span className="ml-1">Save</span>
          </Button>
          <label className="flex items-center gap-1 text-[10px] text-zinc-500 cursor-pointer select-none" title="Silently saves your work every 60 seconds">
            <input type="checkbox" checked={autosave} onChange={(e) => setAutosave(e.target.checked)} data-testid="editor-autosave-toggle" />
            Autosave
          </label>
          <Button 
            variant="outline" 
            size="sm" 
            className={`bg-transparent border-[#1E1E24] hover:bg-white/5 ${project?.published ? 'text-emerald-400 border-emerald-500/50' : ''}`}
            onClick={togglePublish}
            disabled={publishing}
            data-testid="publish-btn"
          >
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : project?.published ? <Check className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
            <span className="ml-1">{project?.published ? 'Published' : 'Publish'}</span>
          </Button>
          <Button size="sm" className="bg-[#22C55E] text-black hover:bg-[#22C55E]/90" onClick={async () => { await saveProject(); navigate(`/play/${projectId}`); }} data-testid="editor-play-btn">
            <Play className="w-4 h-4 mr-1" /> Play
          </Button>
          {/* Consolidated Export ▾ dropdown */}
          <div className="relative" data-testid="editor-export-wrap">
            <select
              onChange={(e) => {
                const fmt = e.target.value;
                if (!fmt) return;
                if (['webbuild', 'zip', 'gltf'].includes(fmt)) {
                  const path = fmt === 'webbuild' ? 'web' : fmt;
                  window.open(`${API}/export/${projectId}/${path}`, '_blank');
                  toast.success(fmt === 'webbuild' ? 'Standalone web build downloading — plays offline' : fmt === 'zip' ? 'Full package (web + Godot + Unity + glTF + assets) downloading' : 'glTF 2.0 scene downloading');
                  e.target.value = '';
                  return;
                }
                const ext = fmt === 'html5' ? 'html' : fmt === 'json' ? 'json' : fmt === 'godot' ? 'tscn' : 'cs';
                const filename = fmt === 'unity' ? `PlayMoldScene_${projectId}.cs` : `${project?.name || 'game'}.${ext}`;
                const a = document.createElement('a');
                a.href = `${API}/projects/${projectId}/export?fmt=${fmt}`;
                a.download = filename;
                document.body.appendChild(a); a.click(); a.remove();
                if (fmt === 'godot') {
                  toast.success('Godot .tscn downloaded', {
                    description: 'Textures loaded via optional GDScript loader.',
                    action: {
                      label: 'Add loader script',
                      onClick: () => {
                        const gd = [
                          "extends Node3D",
                          "# PlayMold Godot texture loader — attach to Root",
                          "func _ready():",
                          "    for child in get_children():",
                          "        var url = child.get_meta('asset_url') if child.has_meta('asset_url') else ''",
                          "        if url != '':",
                          "            var req := HTTPRequest.new()",
                          "            add_child(req)",
                          "            req.request_completed.connect(func(_r, code, _h, body):",
                          "                if code == 200:",
                          "                    var img := Image.new()",
                          "                    img.load_png_from_buffer(body)",
                          "                    var tex := ImageTexture.create_from_image(img)",
                          "                    var mat = child.material_override",
                          "                    if mat and mat is StandardMaterial3D:",
                          "                        mat.albedo_texture = tex",
                          "            )",
                          "            req.request(url)",
                        ].join('\n');
                        const blob = new Blob([gd], { type: 'text/plain' });
                        const b = document.createElement('a');
                        b.href = URL.createObjectURL(blob);
                        b.download = 'playmold_loader.gd';
                        document.body.appendChild(b); b.click(); b.remove();
                        toast.success('playmold_loader.gd downloaded — attach it to Root in Godot');
                      },
                    },
                    duration: 10000,
                  });
                } else {
                  toast.success(`Exporting ${fmt.toUpperCase()}…`);
                }
                e.target.value = '';
              }}
              defaultValue=""
              className="h-9 bg-transparent border border-[#1E1E24] hover:bg-white/5 text-xs text-white rounded-md px-2 pr-6 cursor-pointer appearance-none"
              data-testid="editor-export-select"
              aria-label="Export format"
              title="Export as…"
            >
              <option value="" className="bg-[#0A0A0B]">⬇ Export ▾</option>
              <option value="webbuild" className="bg-[#0A0A0B]">Web Build (offline, standalone)</option>
              <option value="zip" className="bg-[#0A0A0B]">Full ZIP (web+Godot+Unity+glTF)</option>
              <option value="gltf" className="bg-[#0A0A0B]">glTF 2.0 scene</option>
              <option value="html5" className="bg-[#0A0A0B]">HTML5 (playable)</option>
              <option value="json" className="bg-[#0A0A0B]">JSON (raw scene)</option>
              <option value="godot" className="bg-[#0A0A0B]">Godot 4 (.tscn)</option>
              <option value="unity" className="bg-[#0A0A0B]">Unity C# (.cs)</option>
            </select>
          </div>
          <Button 
            size="sm" 
            variant="ghost"
            className="text-zinc-500 hover:text-red-500"
            onClick={async () => {
              const tier = (user?.tier || 'free').toLowerCase();
              const role = user?.role;
              const isPremium = role === 'admin' || tier === 'pro' || tier === 'studio';
              const days = role === 'admin' ? 365 : tier === 'studio' ? 90 : tier === 'pro' ? 60 : 14;
              const msg = isPremium
                ? `This will move the project to your Trash. Auto-deletion in ${days} days.\nGo to /trash if you want to delete it permanently.`
                : `Delete this project? It will be moved to Trash and auto-deleted after ${days} days.`;
              if (window.confirm(msg)) {
                try {
                  await axios.delete(`${API}/projects/${projectId}`, { withCredentials: true });
                  toast.success('Project deleted');
                  navigate('/dashboard');
                } catch (e) {
                  toast.error('Failed to delete');
                }
              }
            }}
            data-testid="delete-project-btn"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Main Editor Layout */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Left Panel - Hierarchy — collapses to top strip on mobile */}
        <div className="w-full md:w-56 max-h-[28vh] md:max-h-none border-b md:border-b-0 md:border-r border-[#1E1E24] bg-[#0A0A0B] flex flex-col shrink-0">
          {/* Levels manager — create/edit/switch multi-level games manually */}
          <div className="p-2 border-b border-[#1E1E24] space-y-1" data-testid="editor-levels-panel">
            <div className="flex items-center gap-1">
              <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono flex-1">Levels ({Math.max(1, getStages().length)})</span>
              <button onClick={addStage} title="Add a level (duplicates the current one as a starting point)" className="text-zinc-400 hover:text-[#06B6D4] text-sm px-1 leading-none" data-testid="editor-add-level">＋</button>
              <button onClick={removeStage} title="Delete this level" className="text-zinc-400 hover:text-red-400 text-xs px-1 leading-none" data-testid="editor-remove-level">🗑</button>
            </div>
            <div className="space-y-0.5" data-testid="editor-level-list">
              {(getStages().length ? getStages() : [{ name: 'Level 1' }]).map((s, i) => (
                <div
                  key={i}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('text/level-index', String(i))}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = parseInt(e.dataTransfer.getData('text/level-index'), 10);
                    if (!Number.isNaN(from)) reorderStage(from, i);
                  }}
                  onClick={() => switchStage(i)}
                  className={`flex items-center gap-1 px-1.5 py-1 rounded text-[11px] font-mono cursor-pointer border transition-colors ${editingStage === i ? 'border-[#06B6D4]/60 bg-[#06B6D4]/10 text-white' : 'border-transparent text-zinc-400 hover:bg-white/5'}`}
                  title="Click to edit · Drag to reorder"
                  data-testid={`editor-level-row-${i}`}
                >
                  <span className="cursor-grab text-zinc-600 select-none">⋮⋮</span>
                  <span className="truncate flex-1">{`${i + 1}. ${s.name || `Level ${i + 1}`}`}</span>
                </div>
              ))}
            </div>
            {getStages().length > 0 && (
              <>
                <Input
                  value={getStages()[editingStage]?.name || ''}
                  placeholder={`Level ${editingStage + 1} name`}
                  onChange={(e) => updateStageMeta('name', e.target.value)}
                  className="h-6 bg-transparent border-[#1E1E24] text-white text-[10px] font-mono"
                  data-testid="editor-level-name"
                />
                <textarea
                  value={getStages()[editingStage]?.story || ''}
                  placeholder="Storyline shown when this level begins…"
                  onChange={(e) => updateStageMeta('story', e.target.value)}
                  className="w-full h-12 bg-[#121214] border border-[#1E1E24] rounded text-[10px] text-zinc-300 px-2 py-1 resize-none focus:outline-none focus:border-[#06B6D4]/50"
                  data-testid="editor-level-story"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="color"
                    value={getStages()[editingStage]?.sky_color || project?.game_data?.settings?.sky_color || '#0A0A1E'}
                    onChange={(e) => updateStageMeta('sky_color', e.target.value)}
                    className="w-7 h-6 bg-transparent border border-[#1E1E24] rounded cursor-pointer p-0"
                    title="This level's sky color"
                    data-testid="editor-level-sky"
                  />
                  <select
                    value={getStages()[editingStage]?.music || ''}
                    onChange={(e) => updateStageMeta('music', e.target.value)}
                    className="flex-1 h-6 bg-[#121214] border border-[#1E1E24] text-zinc-300 text-[10px] rounded px-1 cursor-pointer"
                    title="This level's music mood"
                    data-testid="editor-level-music"
                  >
                    <option value="">Music: auto</option>
                    <option value="adventure">Music: Adventure</option>
                    <option value="chill">Music: Chill</option>
                    <option value="dungeon">Music: Dungeon</option>
                    <option value="boss">Music: Boss</option>
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <select
                    value={getStages()[editingStage]?.weather || ''}
                    onChange={(e) => updateStageMeta('weather', e.target.value)}
                    className="flex-1 h-6 bg-[#121214] border border-[#1E1E24] text-zinc-300 text-[10px] rounded px-1 cursor-pointer"
                    title="This level's weather effect"
                    data-testid="editor-level-weather"
                  >
                    <option value="">Weather: none</option>
                    <option value="rain">Weather: 🌧 Rain</option>
                    <option value="snow">Weather: ❄ Snow</option>
                    <option value="embers">Weather: 🔥 Embers</option>
                  </select>
                </div>
              </>
            )}
          </div>
          <div className="editor-panel-header flex items-center justify-between">
            <span>Scene Hierarchy</span>
            <span className="text-[9px] font-mono text-zinc-500" data-testid="scene-object-count">
              {project?.game_data?.objects?.length || 0} obj
            </span>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2">
              {(() => {
                const objs = project?.game_data?.objects || [];
                const groups = {};
                const loose = [];
                objs.forEach(o => { if (o.group) { (groups[o.group] = groups[o.group] || []).push(o); } else loose.push(o); });
                const row = (obj) => (
                  <div
                    key={obj.name}
                    className={`hierarchy-item ${selectedObject === obj.name || multiSel.includes(obj.name) ? 'selected' : ''}`}
                    onClick={(e) => {
                      if (e.ctrlKey || e.metaKey || e.shiftKey) {
                        setMultiSel(prev => {
                          // Fold the plain-clicked selection into the multi-selection on the first ctrl+click
                          let base = prev;
                          if (!prev.length && selectedObject && selectedObject !== obj.name) base = [selectedObject];
                          return base.includes(obj.name) ? base.filter(n => n !== obj.name) : [...base, obj.name];
                        });
                      } else {
                        setMultiSel([]);
                        setSelectedObject(obj.name);
                      }
                    }}
                    onDoubleClick={(e) => { e.stopPropagation(); setRenamingObj(obj.name); setRenameVal(obj.name); }}
                    title="Click to select · Ctrl+Click multi-select · Double-click to rename"
                    data-testid={`hierarchy-${obj.name}`}
                  >
                    <span className="text-sm">{objectIcons[obj.type] || '📦'}</span>
                    {renamingObj === obj.name ? (
                      <input
                        autoFocus
                        value={renameVal}
                        onChange={(e) => setRenameVal(e.target.value)}
                        onBlur={commitRename}
                        onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingObj(null); }}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 min-w-0 bg-[#121214] border border-[#06B6D4] rounded px-1 text-xs text-white font-mono"
                        data-testid="hierarchy-rename-input"
                      />
                    ) : (
                      <span className="truncate text-sm">{obj.name}</span>
                    )}
                  </div>
                );
                return (
                  <>
                    {Object.entries(groups).map(([gname, members]) => (
                      <div key={gname} className="mb-1">
                        <div
                          className="flex items-center gap-1 px-1 py-1 text-[11px] text-amber-300/90 cursor-pointer hover:bg-white/5 rounded"
                          onClick={() => setCollapsedGroups(prev => ({ ...prev, [gname]: !prev[gname] }))}
                          onDoubleClick={(e) => { e.stopPropagation(); setRenamingGroup(gname); setRenameGroupVal(gname); }}
                          title="Click to collapse/expand · Double-click to rename"
                          data-testid={`hierarchy-group-${gname}`}
                        >
                          {collapsedGroups[gname] ? <ChevronRight className="w-3 h-3 shrink-0" /> : <ChevronDown className="w-3 h-3 shrink-0" />}
                          {renamingGroup === gname ? (
                            <input
                              autoFocus
                              value={renameGroupVal}
                              onChange={(e) => setRenameGroupVal(e.target.value)}
                              onBlur={() => { setRenamingGroup(null); renameGroup(gname, renameGroupVal); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { setRenamingGroup(null); renameGroup(gname, renameGroupVal); }
                                if (e.key === 'Escape') setRenamingGroup(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 min-w-0 bg-[#121214] border border-amber-400 rounded px-1 text-[11px] text-white font-mono"
                              data-testid="group-rename-input"
                            />
                          ) : (
                            <span className="font-mono truncate flex-1">🗂 {gname} ({members.length})</span>
                          )}
                          <button
                            className="text-zinc-500 hover:text-white text-[9px] font-mono px-1"
                            title="Select all members"
                            onClick={(e) => { e.stopPropagation(); setMultiSel(members.map(m => m.name)); setSelectedObject(members[0]?.name || null); }}
                            data-testid={`group-select-all-${gname}`}
                          >◎</button>
                          <button
                            className="text-zinc-500 hover:text-[#06B6D4] text-[9px] font-mono px-1"
                            title="Duplicate group"
                            onClick={(e) => { e.stopPropagation(); duplicateGroup(gname); }}
                            data-testid={`group-duplicate-${gname}`}
                          >⧉</button>
                          <button
                            className="text-zinc-500 hover:text-red-400 text-[9px] font-mono px-1"
                            title="Ungroup"
                            onClick={(e) => { e.stopPropagation(); ungroup(gname); }}
                            data-testid={`group-ungroup-${gname}`}
                          >✕</button>
                        </div>
                        {!collapsedGroups[gname] && <div className="ml-3 border-l border-[#1E1E24] pl-1">{members.map(row)}</div>}
                      </div>
                    ))}
                    {loose.map(row)}
                  </>
                );
              })()}
              {(!project?.game_data?.objects || project.game_data.objects.length === 0) && (
                <p className="text-[10px] text-zinc-600 text-center py-4">Scene is empty — click Add Object below</p>
              )}
            </div>
          </ScrollArea>
          
          {/* Scene controls */}
          <div className="p-2 border-t border-[#1E1E24] space-y-1">
            <select
              defaultValue=""
              onChange={(e) => { const type = e.target.value; e.target.value = ''; if (type) addObjectOfType(type); }}
              className="w-full h-8 bg-[#121214] border border-[#1E1E24] text-white text-xs rounded px-2 cursor-pointer"
              data-testid="editor-add-object"
              title="Add an object to the scene"
            >
              <option value="">＋ Add Object ▾</option>
              <optgroup label="Gameplay">
                <option value="player">Player</option>
                <option value="enemy">Enemy</option>
                <option value="npc">NPC</option>
                <option value="collectible">Collectible</option>
                <option value="goal">Goal</option>
                <option value="trigger">Trigger</option>
              </optgroup>
              <optgroup label="Environment">
                <option value="platform">Platform</option>
                <option value="floor">Floor</option>
                <option value="wall">Wall</option>
                <option value="terrain">Terrain</option>
                <option value="background">Background</option>
                <option value="decoration">Decoration</option>
                <option value="sprite">Sprite</option>
                <option value="texture_prop">Textured Prop</option>
                <option value="model_3d">3D Model</option>
                <option value="particle">Particle FX</option>
                <option value="light">Light</option>
              </optgroup>
              <optgroup label="Geometric Primitives">
                <option value="cube">Cube</option>
                <option value="sphere">Sphere</option>
                <option value="cylinder">Cylinder</option>
                <option value="cone">Cone</option>
                <option value="torus">Torus / Ring</option>
                <option value="capsule">Capsule</option>
                <option value="plane">Plane</option>
              </optgroup>
            </select>
            {multiSel.length >= 2 && (
              <Button
                variant="outline" size="sm"
                className="w-full bg-amber-500/10 border-amber-500/40 text-amber-300 hover:bg-amber-500/20 text-xs h-7"
                onClick={groupSelected}
                data-testid="editor-group-btn"
              >
                🗂 Group {multiSel.length} selected
              </Button>
            )}
            <div className="grid grid-cols-3 gap-1">
              {[
                { type: 'particle',    label: 'FX',       color: '#F472B6' },
                { type: 'enemy',       label: 'Enemy',    color: '#EF4444' },
                { type: 'collectible', label: 'Pickup',   color: '#FBBF24' },
              ].map(preset => (
                <Button
                  key={preset.type}
                  variant="outline" size="sm"
                  className="h-7 bg-transparent border-[#1E1E24] text-[10px] hover:bg-white/5"
                  data-testid={`editor-add-${preset.type}`}
                  onClick={() => {
                    pushUndo();
                    const newObj = {
                      type: preset.type,
                      name: `${preset.label}_${Date.now().toString(36).slice(-4)}`,
                      position: [((project?.game_data?.objects?.length || 0) % 9) - 4, 2, 0],
                      scale: [1, 1, 1],
                      color: preset.color,
                      ...(preset.type === 'particle' ? { particles: { emit_rate: 80, capacity: 400, min_size: 0.1, max_size: 0.5, color1: preset.color, color2: '#FFFFFF' } } : {}),
                      ...(preset.type === 'enemy' ? { behavior: 'chase', damage: 10 } : {}),
                    };
                    setProject(prev => ({
                      ...prev,
                      game_data: { ...prev.game_data, objects: [...(prev.game_data?.objects || []), newObj] }
                    }));
                    setSelectedObject(newObj.name);
                  }}
                >
                  + {preset.label}
                </Button>
              ))}
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost" size="sm"
                className="flex-1 text-[10px] text-zinc-400 hover:text-red-300 hover:bg-red-500/10"
                data-testid="editor-clear-scene"
                title="Delete every object in the scene (undoable)"
                disabled={!project?.game_data?.objects?.length}
                onClick={() => {
                  if (!window.confirm('Delete ALL objects from this scene? You can Undo.')) return;
                  pushUndo();
                  setProject(prev => ({
                    ...prev,
                    game_data: { ...prev.game_data, objects: [] }
                  }));
                  setSelectedObject(null);
                  toast.success('Scene cleared');
                }}
              >
                <Trash2 className="w-3 h-3 mr-1" /> Clear
              </Button>
              <Button
                variant="ghost" size="sm"
                className="flex-1 text-[10px] text-zinc-400 hover:text-[#06B6D4] hover:bg-[#06B6D4]/10"
                data-testid="editor-reset-scene"
                title="Reload the project from disk — discard unsaved edits"
                onClick={() => {
                  if (!window.confirm('Reset scene to last saved version? Any unsaved edits will be lost.')) return;
                  setSelectedObject(null);
                  fetchProject();
                  toast.success('Scene reset to last save');
                }}
              >
                <RotateCcw className="w-3 h-3 mr-1" /> Reset
              </Button>
            </div>
          </div>
        </div>

        {/* Center - Canvas */}
        <div className="flex-1 relative bg-[#0A0A0B] min-h-[45vh] md:min-h-0" onDragOver={onCanvasDragOver} onDrop={onCanvasDrop} data-testid="editor-canvas-drop">
          <canvas ref={canvasRef} className="w-full h-full" data-testid="babylon-canvas" />
          
          {/* Toolbar overlay */}
          <div className="absolute top-4 left-4 flex gap-1 bg-[#121214]/90 border border-[#1E1E24] rounded-md p-1 backdrop-blur-sm">
            <Button 
              variant="ghost" 
              size="icon" 
              className={`w-8 h-8 ${gizmoMode === 'position' ? 'bg-[#06B6D4]/20 text-[#06B6D4]' : 'text-zinc-400 hover:text-white'}`}
              onClick={() => switchGizmoMode('position')}
              title="Move (G)"
              data-testid="gizmo-move"
            >
              <Move className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className={`w-8 h-8 ${gizmoMode === 'rotation' ? 'bg-[#06B6D4]/20 text-[#06B6D4]' : 'text-zinc-400 hover:text-white'}`}
              onClick={() => switchGizmoMode('rotation')}
              title="Rotate (R)"
              data-testid="gizmo-rotate"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className={`w-8 h-8 ${gizmoMode === 'scale' ? 'bg-[#06B6D4]/20 text-[#06B6D4]' : 'text-zinc-400 hover:text-white'}`}
              onClick={() => switchGizmoMode('scale')}
              title="Scale (S)"
              data-testid="gizmo-scale"
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Gizmo mode indicator */}
          <div className="absolute top-4 right-4 bg-[#121214]/90 border border-[#1E1E24] rounded-md px-3 py-1.5 backdrop-blur-sm" data-testid="gizmo-mode-indicator">
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Mode: </span>
            <span className="text-xs text-[#06B6D4] font-mono uppercase" data-testid="gizmo-mode-value">{gizmoMode}</span>
          </div>

          {/* Mini live playtest window */}
          {playtestOpen && (
            <div className="absolute bottom-4 right-4 z-30 w-[460px] max-w-[85%] h-[300px] rounded-lg overflow-hidden border border-[#06B6D4]/50 bg-black shadow-2xl flex flex-col" style={{ isolation: 'isolate', transform: 'translateZ(0)' }} data-testid="editor-playtest-panel">
              <div className="h-8 shrink-0 bg-[#0A0A0B] border-b border-[#1E1E24] flex items-center px-2 gap-2">
                <span className="text-[10px] font-mono text-[#06B6D4] uppercase tracking-widest">● Live Playtest</span>
                <button className="ml-auto text-zinc-500 hover:text-white text-xs px-1" onClick={() => setPlaytestKey(k => k + 1)} title="Restart playtest" data-testid="editor-playtest-restart">⟳</button>
                <button className="text-zinc-500 hover:text-white text-xs px-1" onClick={() => window.open(`/play/${projectId}`, '_blank')} title="Open full size">⧉</button>
                <button className="text-zinc-500 hover:text-red-400 text-xs px-1" onClick={() => setPlaytestOpen(false)} data-testid="editor-playtest-close">✕</button>
              </div>
              <iframe key={playtestKey} src={`/play/${projectId}?embed=1`} title="Live playtest" className="flex-1 w-full border-0" />
            </div>
          )}
        </div>

        {/* Right Panel - Properties & Copilot */}
        <div className="w-full md:w-72 max-h-[45vh] md:max-h-none border-t md:border-t-0 md:border-l border-[#1E1E24] bg-[#0A0A0B] flex flex-col shrink-0 overflow-y-auto">
          {/* Properties */}
          <div className="border-b border-[#1E1E24]">
            <div className="editor-panel-header">Properties</div>
            <div className="p-3">
              {selectedObjData ? (
                <div className="space-y-3">
                  <div className="border border-[#1E1E24] rounded-md p-2 space-y-2" data-testid="asset-behavior-section">
                    <p className="text-[9px] uppercase tracking-wider text-[#06B6D4] font-mono">Asset Behavior</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      <div>
                        <label className="text-[9px] text-zinc-500 uppercase block mb-0.5">Mode</label>
                        <select
                          value={selectedObjData.surface_mode || 'solid'}
                          onChange={(e) => { pushUndo(); updateObjectProperty(selectedObjData.name, 'surface_mode', e.target.value); }}
                          className="w-full bg-[#121214] border border-[#1E1E24] rounded-sm px-1 h-6 text-[10px] text-white"
                          data-testid="prop-surface-mode"
                        >
                          <option value="solid">Solid</option>
                          <option value="texture">Texture</option>
                        </select>
                      </div>
                      {(selectedObjData.surface_mode || 'solid') === 'texture' ? (
                        <div>
                          <label className="text-[9px] text-zinc-500 uppercase block mb-0.5">Tiling</label>
                          <select
                            value={selectedObjData.texture_tile === false ? 'single' : 'tiled'}
                            onChange={(e) => { pushUndo(); updateObjectProperty(selectedObjData.name, 'texture_tile', e.target.value === 'tiled'); }}
                            className="w-full bg-[#121214] border border-[#1E1E24] rounded-sm px-1 h-6 text-[10px] text-white"
                            data-testid="prop-texture-tile"
                          >
                            <option value="tiled">Tiled</option>
                            <option value="single">Single</option>
                          </select>
                        </div>
                      ) : (
                        <div>
                          <label className="text-[9px] text-zinc-500 uppercase block mb-0.5">Kind</label>
                          <select
                            value={selectedObjData.solid_kind || 'object'}
                            onChange={(e) => { pushUndo(); updateObjectProperty(selectedObjData.name, 'solid_kind', e.target.value); }}
                            className="w-full bg-[#121214] border border-[#1E1E24] rounded-sm px-1 h-6 text-[10px] text-white"
                            data-testid="prop-solid-kind"
                          >
                            <option value="character">Character</option>
                            <option value="object">Object</option>
                            <option value="background">Background</option>
                          </select>
                        </div>
                      )}
                    </div>
                    {(selectedObjData.surface_mode || 'solid') === 'solid' && (selectedObjData.solid_kind || 'object') === 'character' && (
                      <>
                        <div>
                          <label className="text-[9px] text-zinc-500 uppercase block mb-0.5">Character Role</label>
                          <select
                            value={selectedObjData.role || 'hero'}
                            onChange={(e) => { pushUndo(); updateObjectProperty(selectedObjData.name, 'role', e.target.value); }}
                            className="w-full bg-[#121214] border border-[#1E1E24] rounded-sm px-1 h-6 text-[10px] text-white"
                            data-testid="prop-character-role"
                          >
                            {['hero', 'anti_hero', 'anti_villain', 'villain', 'weapon', 'trophy'].map(r => (
                              <option key={r} value={r}>{r.replace('_', '-')}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[9px] text-zinc-500 uppercase block mb-0.5">
                            Opacity — {Math.round((selectedObjData.opacity ?? 1) * 100)}%
                          </label>
                          <input
                            type="range" min="10" max="100" step="5"
                            value={Math.round((selectedObjData.opacity ?? 1) * 100)}
                            onChange={(e) => updateObjectProperty(selectedObjData.name, 'opacity', Number(e.target.value) / 100)}
                            className="w-full accent-[#06B6D4]"
                            data-testid="prop-opacity-slider"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Name</label>
                    <Input 
                      value={selectedObjData.name} 
                      onChange={(e) => {
                        const newName = e.target.value.trim();
                        if (!newName || newName === selectedObjData.name) return;
                        // Prevent duplicate names
                        if (project.game_data.objects.some(o => o.name === newName)) {
                          toast.error('An object with that name already exists');
                          return;
                        }
                        pushUndo();
                        setProject(prev => ({
                          ...prev,
                          game_data: {
                            ...prev.game_data,
                            objects: prev.game_data.objects.map(o => o.name === selectedObjData.name ? { ...o, name: newName } : o)
                          }
                        }));
                        setSelectedObject(newName);
                      }}
                      className="h-8 bg-transparent border-[#1E1E24] text-white text-sm font-mono"
                      data-testid="editor-object-name"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Type</label>
                    <select
                      value={selectedObjData.type}
                      onChange={(e) => updateObjectProperty(selectedObjData.name, 'type', e.target.value)}
                      className="w-full h-7 bg-[#121214] border border-[#1E1E24] text-[#06B6D4] text-xs font-mono uppercase rounded px-2"
                      data-testid="editor-object-type"
                    >
                      {['player','platform','floor','wall','structure','puzzle_piece','terrain','enemy','npc','collectible','goal','vehicle','sprite','texture_prop','background','model_3d','particle','decoration','trigger','light','cube','cylinder','sphere','cone','torus','capsule','plane','climbable','obstacle'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <label className="flex items-center gap-1 mt-1 text-[10px] text-zinc-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!selectedObjData.is_player}
                        onChange={(e) => {
                          updateObjectProperty(selectedObjData.name, 'is_player', e.target.checked);
                          if (e.target.checked) updateObjectProperty(selectedObjData.name, 'type', 'player');
                        }}
                        data-testid="editor-object-is-player"
                      />
                      Assign role: <span className="text-[#06B6D4] font-mono">Player</span>
                    </label>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Position</label>
                    <div className="grid grid-cols-3 gap-1">
                      {['X', 'Y', 'Z'].map((axis, idx) => (
                        <Input 
                          key={axis}
                          type="number"
                          value={selectedObjData.position?.[idx] || 0}
                          onChange={(e) => {
                            const pos = [...(selectedObjData.position || [0, 0, 0])];
                            pos[idx] = parseFloat(e.target.value) || 0;
                            updateObjectProperty(selectedObjData.name, 'position', pos);
                          }}
                          className="h-7 bg-transparent border-[#1E1E24] text-white text-xs font-mono"
                          data-testid={`editor-pos-${axis.toLowerCase()}`}
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Rotation (rad)</label>
                    <div className="grid grid-cols-3 gap-1">
                      {['X', 'Y', 'Z'].map((axis, idx) => (
                        <Input 
                          key={axis}
                          type="number" step="0.1"
                          value={selectedObjData.rotation?.[idx] || 0}
                          onChange={(e) => {
                            const rot = [...(selectedObjData.rotation || [0, 0, 0])];
                            rot[idx] = parseFloat(e.target.value) || 0;
                            updateObjectProperty(selectedObjData.name, 'rotation', rot);
                          }}
                          className="h-7 bg-transparent border-[#1E1E24] text-white text-xs font-mono"
                          data-testid={`editor-rot-${axis.toLowerCase()}`}
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Scale</label>
                    <div className="grid grid-cols-3 gap-1">
                      {['X', 'Y', 'Z'].map((axis, idx) => (
                        <Input 
                          key={axis}
                          type="number" step="0.1" min="0.05"
                          value={(selectedObjData.scale_abs && selectedObjData.scale) ? selectedObjData.scale[idx] : effectiveScale(selectedObjData)[idx]}
                          onChange={(e) => {
                            const cur = (selectedObjData.scale_abs && selectedObjData.scale) ? [...selectedObjData.scale] : [...effectiveScale(selectedObjData)];
                            cur[idx] = Math.max(0.05, parseFloat(e.target.value) || 0.05);
                            updateObjectProps(selectedObjData.name, { scale: cur, scale_abs: true });
                          }}
                          className="h-7 bg-transparent border-[#1E1E24] text-white text-xs font-mono"
                          data-testid={`editor-scale-${axis.toLowerCase()}`}
                        />
                      ))}
                    </div>
                  </div>
                  {selectedObjData.type === 'light' && (
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Light Intensity</label>
                      <Input
                        type="number" step="0.1" min="0" max="10"
                        value={selectedObjData.intensity ?? 1}
                        onChange={(e) => updateObjectProperty(selectedObjData.name, 'intensity', Math.max(0, parseFloat(e.target.value) || 0))}
                        className="h-7 bg-transparent border-[#1E1E24] text-white text-xs font-mono"
                        data-testid="editor-light-intensity"
                      />
                      <p className="text-[9px] text-zinc-600 mt-1">Color below tints the light · applies in preview · delete the object to remove the light.</p>
                    </div>
                  )}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Color</label>
                    <div className="flex items-center gap-2">
                      <input 
                        type="color" 
                        value={selectedObjData.color || '#06B6D4'}
                        onChange={(e) => updateObjectProperty(selectedObjData.name, 'color', e.target.value)}
                        className="w-8 h-8 rounded-sm border border-[#1E1E24] cursor-pointer"
                      />
                      <span className="text-xs font-mono text-zinc-400">{selectedObjData.color}</span>
                    </div>
                  </div>

                  {/* ==== MATERIAL & TEXTURE MAPS ==== */}
                  <div className="pt-3 border-t border-[#1E1E24]">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-[10px] uppercase tracking-wider text-zinc-500">Textures & Material</label>
                      <div className="flex items-center gap-1 flex-wrap justify-end">
                        {Object.entries(detectMaterialChannels(selectedObjData)).map(([k, v]) => (
                          <span key={k} className={`text-[8px] font-mono uppercase px-1 rounded ${v ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-zinc-600'}`} title={`${k}: ${v ? 'detected' : 'none'}`}>
                            {k === 'roughness' ? 'ROUGH' : k === 'metallic' ? 'METAL' : k === 'uv_tiling' ? 'UV' : k.toUpperCase()}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Texture map URL inputs */}
                    {[
                      ['diffuse',   'Diffuse / Albedo (auto from asset_url)'],
                      ['normal',    'Normal / Bump map'],
                      ['ao',        'Ambient Occlusion'],
                      ['emissive',  'Emissive glow'],
                      ['metallic',  'Metallic'],
                      ['roughness', 'Roughness'],
                    ].map(([key, label]) => (
                      <div key={key} className="mb-2">
                        <label className="text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5 block">{label}</label>
                        <Input
                          type="url"
                          placeholder={key === 'diffuse' ? (selectedObjData.asset_url || 'https://…/tex.png') : 'https://…/map.png'}
                          value={selectedObjData.textures?.[key] || (key === 'diffuse' ? (selectedObjData.textures?.diffuse || '') : '')}
                          onChange={(e) => {
                            const nextTextures = { ...(selectedObjData.textures || {}), [key]: e.target.value || undefined };
                            updateObjectProperty(selectedObjData.name, 'textures', nextTextures);
                          }}
                          className="h-7 bg-transparent border-[#1E1E24] text-white text-[11px] font-mono"
                          data-testid={`texture-input-${key}`}
                        />
                      </div>
                    ))}

                    {/* Alpha mode */}
                    <div className="mb-2">
                      <label className="text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5 block">Alpha Mode</label>
                      <select
                        value={selectedObjData.material?.alpha_mode || selectedObjData.alpha_mode || 'auto'}
                        onChange={(e) => {
                          const mat = { ...(selectedObjData.material || {}), alpha_mode: e.target.value };
                          updateObjectProperty(selectedObjData.name, 'material', mat);
                        }}
                        className="w-full h-7 bg-[#121214] border border-[#1E1E24] text-white text-[11px] rounded px-2"
                        data-testid="material-alpha-mode"
                      >
                        <option value="auto">Auto (use PNG alpha)</option>
                        <option value="opaque">Opaque</option>
                        <option value="cutout">Cutout / Alpha Test</option>
                        <option value="blend">Alpha Blend</option>
                      </select>
                    </div>

                    {/* PBR toggle + metallic/roughness sliders */}
                    <label className="flex items-center gap-2 text-[11px] text-zinc-300 mb-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!(selectedObjData.material?.pbr)}
                        onChange={(e) => updateObjectProperty(selectedObjData.name, 'material', { ...(selectedObjData.material || {}), pbr: e.target.checked })}
                        data-testid="material-pbr-toggle"
                      />
                      Use PBR shading
                    </label>

                    {/* UV tiling */}
                    <div className="grid grid-cols-2 gap-1 mb-2">
                      {['uScale', 'vScale', 'uOffset', 'vOffset'].map(k => (
                        <div key={k}>
                          <label className="text-[9px] uppercase tracking-wider text-zinc-500 block">{k}</label>
                          <Input
                            type="number"
                            step="0.1"
                            value={selectedObjData.uv?.[k] ?? (k.startsWith('u') && k.endsWith('cale') || k.startsWith('v') && k.endsWith('cale') ? 1 : 0)}
                            onChange={(e) => {
                              const nv = { ...(selectedObjData.uv || {}), [k]: parseFloat(e.target.value) };
                              updateObjectProperty(selectedObjData.name, 'uv', nv);
                            }}
                            className="h-6 bg-transparent border-[#1E1E24] text-white text-[10px] font-mono"
                            data-testid={`uv-${k}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ==== PHYSICS & COLLIDER ==== */}
                  <div className="pt-3 border-t border-[#1E1E24]">
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Physics & Collider</label>
                    <div className="grid grid-cols-2 gap-1 mb-2">
                      <div>
                        <label className="text-[9px] uppercase tracking-wider text-zinc-500 block">Collider</label>
                        <select
                          value={selectedObjData.collider || 'none'}
                          onChange={(e) => updateObjectProperty(selectedObjData.name, 'collider', e.target.value === 'none' ? undefined : e.target.value)}
                          className="w-full h-6 bg-[#121214] border border-[#1E1E24] text-white text-[10px] rounded px-1"
                          data-testid="physics-collider"
                        >
                          <option value="none">none</option>
                          <option value="box">box</option>
                          <option value="sphere">sphere</option>
                          <option value="capsule">capsule</option>
                          <option value="mesh">mesh</option>
                        </select>
                      </div>
                      <label className="flex items-center gap-1 text-[10px] text-zinc-300 mt-4 cursor-pointer">
                        <input type="checkbox"
                          checked={!!selectedObjData.is_trigger}
                          onChange={(e) => updateObjectProperty(selectedObjData.name, 'is_trigger', e.target.checked)}
                          data-testid="physics-trigger"
                        />
                        Trigger only
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      {[
                        ['mass', 'Mass', 1],
                        ['gravity_scale', 'Gravity Scale', 1],
                        ['linear_drag', 'Linear Drag', 0],
                        ['angular_drag', 'Angular Drag', 0.05],
                        ['friction', 'Friction', 0.5],
                        ['restitution', 'Bounciness', 0.1],
                      ].map(([key, label, def]) => (
                        <div key={key}>
                          <label className="text-[9px] uppercase tracking-wider text-zinc-500 block">{label}</label>
                          <Input
                            type="number" step="0.05"
                            value={selectedObjData.physics?.[key] ?? def}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              const p = { ...(selectedObjData.physics || {}), [key]: isNaN(val) ? def : val };
                              updateObjectProperty(selectedObjData.name, 'physics', p);
                            }}
                            className="h-6 bg-transparent border-[#1E1E24] text-white text-[10px] font-mono"
                            data-testid={`physics-${key}`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-1 mt-1">
                      <label className="flex items-center gap-1 text-[10px] text-zinc-300 cursor-pointer">
                        <input type="checkbox"
                          checked={!!selectedObjData.physics?.freeze_rotation}
                          onChange={(e) => updateObjectProperty(selectedObjData.name, 'physics', { ...(selectedObjData.physics || {}), freeze_rotation: e.target.checked })}
                          data-testid="physics-freeze-rotation"
                        />
                        Freeze rotation
                      </label>
                      <label className="flex items-center gap-1 text-[10px] text-zinc-300 cursor-pointer">
                        <input type="checkbox"
                          checked={!!selectedObjData.physics?.kinematic}
                          onChange={(e) => updateObjectProperty(selectedObjData.name, 'physics', { ...(selectedObjData.physics || {}), kinematic: e.target.checked })}
                          data-testid="physics-kinematic"
                        />
                        Kinematic
                      </label>
                    </div>
                  </div>

                  {/* ==== ANIMATION (sprites) ==== */}
                  {(selectedObjData.type === 'sprite' || selectedObjData.type === 'texture_prop' || selectedObjData.type === 'background') && (
                    <div className="pt-3 border-t border-[#1E1E24]">
                      <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Animation & Draw</label>
                      <div className="grid grid-cols-2 gap-1">
                        <div>
                          <label className="text-[9px] uppercase text-zinc-500 block">Frames</label>
                          <Input type="number" min="1" max="128"
                            value={selectedObjData.animation?.frames || 1}
                            onChange={(e) => updateObjectProperty(selectedObjData.name, 'animation', { ...(selectedObjData.animation || {}), frames: Math.max(1, parseInt(e.target.value) || 1) })}
                            className="h-6 bg-transparent border-[#1E1E24] text-white text-[10px] font-mono"
                            data-testid="anim-frames"
                          />
                        </div>
                        <div>
                          <label className="text-[9px] uppercase text-zinc-500 block">FPS</label>
                          <Input type="number" min="1" max="60"
                            value={selectedObjData.animation?.fps || 12}
                            onChange={(e) => updateObjectProperty(selectedObjData.name, 'animation', { ...(selectedObjData.animation || {}), fps: Math.max(1, parseInt(e.target.value) || 12) })}
                            className="h-6 bg-transparent border-[#1E1E24] text-white text-[10px] font-mono"
                            data-testid="anim-fps"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-1 mt-1">
                        <label className="flex items-center gap-1 text-[10px] text-zinc-300 cursor-pointer">
                          <input type="checkbox" checked={!!selectedObjData.animation?.loop}
                            onChange={(e) => updateObjectProperty(selectedObjData.name, 'animation', { ...(selectedObjData.animation || {}), loop: e.target.checked })}
                            data-testid="anim-loop"
                          />
                          Loop
                        </label>
                        <label className="flex items-center gap-1 text-[10px] text-zinc-300 cursor-pointer">
                          <input type="checkbox" checked={!!selectedObjData.flip_x}
                            onChange={(e) => updateObjectProperty(selectedObjData.name, 'flip_x', e.target.checked)}
                            data-testid="sprite-flip-x"
                          />
                          Flip X
                        </label>
                      </div>
                      <div>
                        <label className="text-[9px] uppercase text-zinc-500 mt-1 block">Draw Order (Z)</label>
                        <Input type="number"
                          value={selectedObjData.draw_order ?? 0}
                          onChange={(e) => updateObjectProperty(selectedObjData.name, 'draw_order', parseInt(e.target.value) || 0)}
                          className="h-6 bg-transparent border-[#1E1E24] text-white text-[10px] font-mono"
                          data-testid="sprite-draw-order"
                        />
                      </div>
                      {selectedObjData.type === 'background' && (
                        <label className="flex items-center gap-1 text-[10px] text-zinc-300 mt-1 cursor-pointer">
                          <input type="checkbox" checked={!!selectedObjData.parallax}
                            onChange={(e) => updateObjectProperty(selectedObjData.name, 'parallax', e.target.checked)}
                            data-testid="bg-parallax"
                          />
                          Parallax scrolling
                        </label>
                      )}
                      {/* Live sprite-sheet preview when frames > 1 */}
                      {(selectedObjData.textures?.diffuse || selectedObjData.asset_url) && (selectedObjData.animation?.frames || 1) > 1 && (
                        <SpriteTimeline
                          url={selectedObjData.textures?.diffuse || selectedObjData.asset_url}
                          frames={selectedObjData.animation?.frames || 1}
                          fps={selectedObjData.animation?.fps || 12}
                          loop={selectedObjData.animation?.loop !== false}
                        />
                      )}
                    </div>
                  )}

                  {/* ==== PARTICLE TUNING ==== */}
                  {selectedObjData.type === 'particle' && (
                    <div className="pt-3 border-t border-[#1E1E24]">
                      <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Particle Effect</label>
                      <div>
                        <label className="text-[9px] uppercase text-zinc-500 block">FX Preset</label>
                        <select
                          onChange={(e) => {
                            const presets = {
                              fire:    { emit_rate: 200, capacity: 400, min_size: 0.15, max_size: 0.6, min_lifetime: 0.3, max_lifetime: 1.2, color1: '#F97316', color2: '#FCD34D' },
                              smoke:   { emit_rate: 40,  capacity: 250, min_size: 0.3,  max_size: 1.0, min_lifetime: 1.5, max_lifetime: 3.0, color1: '#71717A', color2: '#3F3F46' },
                              sparkle: { emit_rate: 80,  capacity: 200, min_size: 0.05, max_size: 0.2, min_lifetime: 0.4, max_lifetime: 0.9, color1: '#FCD34D', color2: '#F9A8D4' },
                              magic:   { emit_rate: 120, capacity: 300, min_size: 0.1,  max_size: 0.4, min_lifetime: 0.8, max_lifetime: 1.8, color1: '#A78BFA', color2: '#38BDF8' },
                              rain:    { emit_rate: 300, capacity: 600, min_size: 0.05, max_size: 0.1, min_lifetime: 0.6, max_lifetime: 1.0, color1: '#60A5FA', color2: '#93C5FD' },
                              explosion:{emit_rate: 500, capacity: 500, min_size: 0.2,  max_size: 0.8, min_lifetime: 0.15, max_lifetime: 0.5, color1: '#EF4444', color2: '#F97316' },
                              snow:    { emit_rate: 60,  capacity: 300, min_size: 0.08, max_size: 0.18, min_lifetime: 3.0, max_lifetime: 5.0, color1: '#F1F5F9', color2: '#E2E8F0' },
                              embers:  { emit_rate: 50,  capacity: 250, min_size: 0.12, max_size: 0.45, min_lifetime: 1.0, max_lifetime: 2.2, color1: '#FF8A3D', color2: '#F43F5E' },
                              fireflies:{emit_rate: 22,  capacity: 120, min_size: 0.08, max_size: 0.3,  min_lifetime: 2.0, max_lifetime: 4.5, color1: '#BEF264', color2: '#4ADE80' },
                            };
                            const preset = presets[e.target.value];
                            if (preset) updateObjectProperty(selectedObjData.name, 'particles', { ...(selectedObjData.particles || {}), ...preset });
                          }}
                          defaultValue=""
                          className="w-full h-7 bg-[#121214] border border-[#1E1E24] text-white text-xs rounded px-2 mb-2"
                          data-testid="particles-preset"
                        >
                          <option value="">— Preset —</option>
                          <option value="fire">🔥 Fire</option>
                          <option value="smoke">💨 Smoke</option>
                          <option value="sparkle">✨ Sparkle</option>
                          <option value="magic">🪄 Magic</option>
                          <option value="rain">🌧️ Rain</option>
                          <option value="explosion">💥 Explosion</option>
                          <option value="snow">❄️ Snow</option>
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        {[
                          ['emit_rate',   'Emit rate',    60],
                          ['capacity',    'Capacity',     300],
                          ['min_size',    'Min size',     0.1],
                          ['max_size',    'Max size',     0.5],
                          ['min_lifetime','Min lifetime', 0.3],
                          ['max_lifetime','Max lifetime', 1.5],
                        ].map(([key, label, def]) => (
                          <div key={key}>
                            <label className="text-[9px] uppercase text-zinc-500 block">{label}</label>
                            <Input
                              type="number" step="0.05"
                              value={selectedObjData.particles?.[key] ?? def}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                const p = { ...(selectedObjData.particles || {}), [key]: isNaN(val) ? def : val };
                                updateObjectProperty(selectedObjData.name, 'particles', p);
                              }}
                              className="h-6 bg-transparent border-[#1E1E24] text-white text-[10px] font-mono"
                              data-testid={`particles-${key}`}
                            />
                          </div>
                        ))}
                        <div>
                          <label className="text-[9px] uppercase text-zinc-500 block">Color 1</label>
                          <input type="color"
                            value={selectedObjData.particles?.color1 || '#06B6D4'}
                            onChange={(e) => updateObjectProperty(selectedObjData.name, 'particles', { ...(selectedObjData.particles || {}), color1: e.target.value })}
                            className="w-full h-6 rounded-sm border border-[#1E1E24] cursor-pointer"
                            data-testid="particles-color1"
                          />
                        </div>
                        <div>
                          <label className="text-[9px] uppercase text-zinc-500 block">Color 2</label>
                          <input type="color"
                            value={selectedObjData.particles?.color2 || '#FFFFFF'}
                            onChange={(e) => updateObjectProperty(selectedObjData.name, 'particles', { ...(selectedObjData.particles || {}), color2: e.target.value })}
                            className="w-full h-6 rounded-sm border border-[#1E1E24] cursor-pointer"
                            data-testid="particles-color2"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ==== SCRIPTS / BEHAVIORS ==== */}
                  <div className="pt-3 border-t border-[#1E1E24]">
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Scripts / Behaviors</label>
                    <div className="space-y-1">
                      {(selectedObjData.scripts || []).map((s, i) => (
                        <div key={i} className="flex items-center gap-1 text-[11px] bg-[#121214] border border-[#1E1E24] rounded px-2 py-1" data-testid={`script-${i}`}>
                          <Bot className="w-3 h-3 text-[#06B6D4] shrink-0" />
                          <span className="truncate flex-1 font-mono text-zinc-300" title={s.name}>{s.name}</span>
                          <button
                            className="text-zinc-500 hover:text-[#06B6D4] text-[10px] mr-1"
                            onClick={() => {
                              const next = window.prompt('Edit this behavior:', s.body || s.name);
                              if (!next || next === s.body) return;
                              const nextScripts = (selectedObjData.scripts || []).map((sc, j) =>
                                j === i ? { ...sc, name: next.slice(0, 40), body: next, compiled: undefined, code: undefined } : sc);
                              updateObjectProperty(selectedObjData.name, 'scripts', nextScripts);
                              toast.success('Behavior updated — it recompiles on the next save');
                            }}
                            data-testid={`script-edit-${i}`}
                          >Edit</button>
                          <button
                            className="text-zinc-500 hover:text-red-400 text-[10px]"
                            onClick={() => {
                              const nextScripts = [...(selectedObjData.scripts || [])];
                              nextScripts.splice(i, 1);
                              updateObjectProperty(selectedObjData.name, 'scripts', nextScripts);
                            }}
                            data-testid={`script-remove-${i}`}
                          >Remove</button>
                        </div>
                      ))}
                      {(!selectedObjData.scripts || selectedObjData.scripts.length === 0) && (
                        <p className="text-[10px] text-zinc-600">No scripts attached.</p>
                      )}
                    </div>
                    <Button
                      variant="outline" size="sm"
                      className="w-full mt-2 bg-transparent border-[#1E1E24] hover:bg-[#06B6D4]/10 text-[10px] h-7"
                      data-testid="script-add-btn"
                      onClick={() => {
                        const kind = window.prompt('Describe the behavior in one sentence:\n(e.g. "chase the player", "spin every second", "on-collide reduce player health by 10")');
                        if (!kind) return;
                        const nextScripts = [
                          ...(selectedObjData.scripts || []),
                          { name: kind.slice(0, 40), body: kind, source: 'ai_stub', created_at: new Date().toISOString() },
                        ];
                        updateObjectProperty(selectedObjData.name, 'scripts', nextScripts);
                        toast.success('Behavior queued — the AI Copilot will attach it on save');
                      }}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add Behavior via AI
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-[#06B6D4] hover:text-white hover:bg-[#06B6D4]/10 text-xs"
                      data-testid="editor-duplicate-object"
                      onClick={() => {
                        pushUndo();
                        const base = selectedObjData.name.replace(/_copy_\w+$/, '');
                        const clone = {
                          ...JSON.parse(JSON.stringify(selectedObjData)),
                          name: `${base}_copy_${Date.now().toString(36).slice(-4)}`,
                          position: [
                            (selectedObjData.position?.[0] || 0) + 1.5,
                            selectedObjData.position?.[1] || 0,
                            (selectedObjData.position?.[2] || 0) + 1.5,
                          ],
                        };
                        setProject(prev => ({
                          ...prev,
                          game_data: {
                            ...prev.game_data,
                            objects: [...prev.game_data.objects, clone]
                          }
                        }));
                        setSelectedObject(clone.name);
                        toast.success('Duplicated');
                      }}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Duplicate
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs"
                      onClick={() => {
                        pushUndo();
                        setProject(prev => ({
                          ...prev,
                          game_data: {
                            ...prev.game_data,
                            objects: prev.game_data.objects.filter(o => o.name !== selectedObjData.name)
                          }
                        }));
                        setSelectedObject(null);
                      }}
                      data-testid="editor-delete-object"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>

                  {/* Surface Texture — any asset acts as a real texture; tiled or flat */}
                  <div className="space-y-2 pt-2 border-t border-[#1E1E24]" data-testid="editor-surface-texture">
                    <p className="text-[10px] text-zinc-500 uppercase font-mono">Surface Texture</p>
                    <select
                      value={selectedObjData.surface_mode === 'texture' ? (selectedObjData.asset_url || '') : ''}
                      onChange={(e) => applySurfaceTexture(selectedObjData.name, e.target.value)}
                      className="w-full bg-[#121214] border border-[#1E1E24] rounded-sm px-1.5 h-7 text-[11px] text-white"
                      data-testid="editor-texture-select"
                    >
                      <option value="">none (solid color)</option>
                      {selectedObjData.asset_url && <option value={selectedObjData.asset_url}>own sprite art</option>}
                      {(project?.game_data?.textures || []).map(t => (
                        <option key={t.asset_id || t.url} value={t.url}>{t.name || 'texture'}</option>
                      ))}
                    </select>
                    {(selectedObjData.surface_mode === 'texture' || (selectedObjData.type === 'background' && selectedObjData.asset_url)) && (
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={selectedObjData.texture_mode || 'tiled'}
                          onChange={(e) => updateObjectProperty(selectedObjData.name, 'texture_mode', e.target.value)}
                          className="bg-[#121214] border border-[#1E1E24] rounded-sm px-1.5 h-7 text-[11px] text-white"
                          data-testid="editor-texture-mode"
                        >
                          <option value="tiled">tiled (repeats)</option>
                          <option value="flat">flat (stretch once)</option>
                        </select>
                        <input
                          type="number" min="1" max="32"
                          value={selectedObjData.uv?.uScale || 4}
                          onChange={(e) => { const n = Math.max(1, Number(e.target.value) || 1); updateObjectProperty(selectedObjData.name, 'uv', { uScale: n, vScale: n }); }}
                          disabled={(selectedObjData.texture_mode || 'tiled') === 'flat'}
                          className="bg-[#121214] border border-[#1E1E24] rounded-sm px-1.5 h-7 text-[11px] text-white disabled:opacity-40"
                          title="Tile repeat count"
                          data-testid="editor-texture-repeat"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3" data-testid="editor-game-settings">
                  <p className="text-[10px] text-zinc-500 uppercase font-mono">Game Settings</p>
                  <p className="text-xs text-zinc-500">Click any object in the scene or hierarchy to edit. Below are global runtime values.</p>
                  {[
                    ['starting_health', 'Starting Health', 1, 999, 100],
                    ['win_score',        'Score to Win',   0, 9999, 100],
                    ['gravity',          'Gravity (m/s²)', -30, 0, -9.8],
                    ['jump_force',       'Jump Force',     1, 30, 8],
                    ['player_speed',     'Player Speed',   0.1, 20, 5],
                    ['ambient_intensity','Ambient Light ×', 0, 3, 1],
                  ].map(([key, label, min, max, def]) => {
                    const settings = project?.game_data?.settings || {};
                    const v = settings[key] ?? def;
                    return (
                      <div key={key}>
                        <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">{label}</label>
                        <Input
                          type="number" min={min} max={max} step={key === 'gravity' || key === 'ambient_intensity' ? 0.1 : 1}
                          value={v}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            const nextSettings = { ...settings, [key]: isNaN(val) ? def : val };
                            pushUndo();
                            setProject(prev => ({
                              ...prev,
                              game_data: { ...prev.game_data, settings: nextSettings }
                            }));
                          }}
                          className="h-7 bg-transparent border-[#1E1E24] text-white text-xs font-mono"
                          data-testid={`game-setting-${key}`}
                        />
                      </div>
                    );
                  })}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">FX Presets</label>
                    <div className="grid grid-cols-2 gap-1" data-testid="editor-fx-presets">
                      {[
                        ['Neon Night', { bloom: true, bloom_intensity: 0.8, vignette: true, sky_color: '#0A0A1E', fog: false }],
                        ['Sunny Day', { bloom: true, bloom_intensity: 0.25, vignette: false, sky_color: '#7DD3FC', fog: false }],
                        ['Horror Fog', { bloom: false, vignette: true, sky_color: '#0C0A09', fog: true }],
                        ['Dream Glow', { bloom: true, bloom_intensity: 1.1, vignette: false, sky_color: '#4C1D95', fog: true }],
                        ['Retro Flat', { bloom: false, vignette: false, sky_color: '#1E293B', fog: false }],
                        ['Inferno', { bloom: true, bloom_intensity: 0.9, vignette: true, sky_color: '#450A0A', fog: true }],
                      ].map(([label, fx]) => (
                        <button
                          key={label}
                          onClick={() => {
                            pushUndo();
                            setProject(prev => ({ ...prev, game_data: { ...prev.game_data, settings: { ...(prev.game_data?.settings || {}), ...fx } } }));
                            toast.success(`FX preset "${label}" applied`);
                          }}
                          className="text-[10px] py-1.5 rounded-sm border border-[#1E1E24] text-zinc-300 hover:border-[#06B6D4] hover:text-white transition-colors"
                          data-testid={`fx-preset-${label.replace(/\s/g, '-').toLowerCase()}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full bg-transparent border-[#1E1E24] hover:bg-white/5 text-xs"
                    data-testid="editor-reachability-check"
                    onClick={() => {
                      const settings = project?.game_data?.settings || {};
                      const jf = settings.jump_force ?? 8;
                      const sp = settings.player_speed ?? 5;
                      const maxDist = sp * (2 * jf / 20) * 0.85;
                      const plats = (project?.game_data?.objects || [])
                        .filter(o => ['platform', 'floor'].includes(o.type))
                        .map(o => ({ x: (o.position || [0, 0, 0])[0], y: (o.position || [0, 0, 0])[1], w: (o.scale || [1, 1, 1])[0] }))
                        .sort((a, b) => a.x - b.x);
                      let worst = 0;
                      for (let i = 0; i < plats.length - 1; i++) {
                        const gap = (plats[i + 1].x - plats[i + 1].w / 2) - (plats[i].x + plats[i].w / 2);
                        const rise = Math.max(0, plats[i + 1].y - plats[i].y);
                        worst = Math.max(worst, gap + rise * 0.8);
                      }
                      if (plats.length < 2) { toast.info('Fewer than 2 platforms — nothing to check'); return; }
                      if (worst <= maxDist) {
                        toast.success(`All platforms reachable ✓ (worst gap ${worst.toFixed(1)}u ≤ max jump ${maxDist.toFixed(1)}u)`);
                      } else {
                        const neededJf = Math.min(20, Math.ceil((worst / 0.85 / sp) * 20 / 2));
                        toast.error(`Impossible gap detected: ${worst.toFixed(1)}u > max jump ${maxDist.toFixed(1)}u`, {
                          action: {
                            label: `Auto-fix (jump ${neededJf})`,
                            onClick: () => {
                              pushUndo();
                              setProject(prev => ({ ...prev, game_data: { ...prev.game_data, settings: { ...(prev.game_data?.settings || {}), jump_force: neededJf } } }));
                              toast.success(`Jump force raised to ${neededJf} — level is now completable`);
                            },
                          },
                          duration: 10000,
                        });
                      }
                    }}
                  >
                    ⤴ Check Reachability
                  </Button>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Sky Color</label>
                    <input
                      type="color"
                      value={project?.game_data?.settings?.sky_color || '#0A0A1E'}
                      onChange={(e) => {
                        const nextSettings = { ...(project?.game_data?.settings || {}), sky_color: e.target.value };
                        pushUndo();
                        setProject(prev => ({ ...prev, game_data: { ...prev.game_data, settings: nextSettings }}));
                      }}
                      className="w-full h-8 rounded-sm border border-[#1E1E24] cursor-pointer"
                      data-testid="game-setting-sky-color"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-[11px] text-zinc-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!project?.game_data?.settings?.fog}
                      onChange={(e) => {
                        const nextSettings = { ...(project?.game_data?.settings || {}), fog: e.target.checked };
                        pushUndo();
                        setProject(prev => ({ ...prev, game_data: { ...prev.game_data, settings: nextSettings }}));
                      }}
                      data-testid="game-setting-fog"
                    />
                    Enable Fog
                  </label>

                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Camera</label>
                    <select
                      value={project?.game_data?.settings?.camera_type || 'follow'}
                      onChange={(e) => {
                        const nextSettings = { ...(project?.game_data?.settings || {}), camera_type: e.target.value };
                        pushUndo();
                        setProject(prev => ({ ...prev, game_data: { ...prev.game_data, settings: nextSettings }}));
                      }}
                      className="w-full h-7 bg-[#121214] border border-[#1E1E24] text-white text-xs rounded px-2"
                      data-testid="game-setting-camera"
                    >
                      <option value="follow">Follow (3rd person, dynamic)</option>
                      <option value="third_person">Third person orbit</option>
                      <option value="first_person">First person</option>
                      <option value="isometric">Isometric</option>
                      <option value="2d">2D / Orthographic (side-scroll)</option>
                    </select>
                  </div>

                  <div className="pt-2 border-t border-[#1E1E24] space-y-1" data-testid="editor-weapon-settings">
                    <label className="flex items-center gap-2 text-[11px] text-zinc-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={project?.game_data?.settings?.weapons_enabled ?? !!project?.game_data?.projectDefinition?.armed}
                        onChange={(e) => {
                          const nextSettings = { ...(project?.game_data?.settings || {}), weapons_enabled: e.target.checked };
                          pushUndo();
                          setProject(prev => ({ ...prev, game_data: { ...prev.game_data, settings: nextSettings }}));
                        }}
                        data-testid="game-setting-weapons"
                      />
                      Weapons enabled (player can shoot)
                    </label>
                    <label className="text-[9px] uppercase tracking-wider text-zinc-500 block">Projectile origin offset (X / Y / Z)</label>
                    <div className="grid grid-cols-3 gap-1">
                      {[0, 1, 2].map(i => (
                        <Input
                          key={i}
                          type="number" step="0.1"
                          value={(project?.game_data?.settings?.muzzle_offset || [0, 0.3, 0])[i]}
                          onChange={(e) => {
                            const mo = [...(project?.game_data?.settings?.muzzle_offset || [0, 0.3, 0])];
                            mo[i] = parseFloat(e.target.value) || 0;
                            const nextSettings = { ...(project?.game_data?.settings || {}), muzzle_offset: mo };
                            pushUndo();
                            setProject(prev => ({ ...prev, game_data: { ...prev.game_data, settings: nextSettings }}));
                          }}
                          className="h-6 bg-transparent border-[#1E1E24] text-white text-[10px] font-mono"
                          data-testid={`muzzle-offset-${['x','y','z'][i]}`}
                        />
                      ))}
                    </div>
                    <p className="text-[9px] text-zinc-600">Set where projectiles spawn relative to the player — e.g. the tip of a gun.</p>
                  </div>

                  <div className="grid grid-cols-2 gap-1">
                    <label className="flex items-center gap-1 text-[10px] text-zinc-300 cursor-pointer">
                      <input type="checkbox"
                        checked={project?.game_data?.settings?.bloom !== false}
                        onChange={(e) => {
                          const nextSettings = { ...(project?.game_data?.settings || {}), bloom: e.target.checked };
                          pushUndo();
                          setProject(prev => ({ ...prev, game_data: { ...prev.game_data, settings: nextSettings }}));
                        }}
                        data-testid="game-setting-bloom"
                      />
                      Bloom
                    </label>
                    <label className="flex items-center gap-1 text-[10px] text-zinc-300 cursor-pointer">
                      <input type="checkbox"
                        checked={!!project?.game_data?.settings?.vignette}
                        onChange={(e) => {
                          const nextSettings = { ...(project?.game_data?.settings || {}), vignette: e.target.checked };
                          pushUndo();
                          setProject(prev => ({ ...prev, game_data: { ...prev.game_data, settings: nextSettings }}));
                        }}
                        data-testid="game-setting-vignette"
                      />
                      Vignette
                    </label>
                  </div>

                  {/* AR / VR / WebXR export toggles — free experimentation for everyone,
                      polished export gated to Pro/Studio tiers via the pricing page. */}
                  <div className="grid grid-cols-2 gap-1">
                    <label className="flex items-center gap-1 text-[10px] text-zinc-300 cursor-pointer" title="Enable WebXR VR immersive mode at runtime">
                      <input type="checkbox"
                        checked={!!project?.game_data?.settings?.vr_enabled}
                        onChange={(e) => {
                          const nextSettings = { ...(project?.game_data?.settings || {}), vr_enabled: e.target.checked };
                          pushUndo();
                          setProject(prev => ({ ...prev, game_data: { ...prev.game_data, settings: nextSettings }}));
                        }}
                        data-testid="game-setting-vr"
                      />
                      VR Mode (WebXR)
                    </label>
                    <label className="flex items-center gap-1 text-[10px] text-zinc-300 cursor-pointer" title="Enable AR passthrough on supported devices">
                      <input type="checkbox"
                        checked={!!project?.game_data?.settings?.ar_enabled}
                        onChange={(e) => {
                          const nextSettings = { ...(project?.game_data?.settings || {}), ar_enabled: e.target.checked };
                          pushUndo();
                          setProject(prev => ({ ...prev, game_data: { ...prev.game_data, settings: nextSettings }}));
                        }}
                        data-testid="game-setting-ar"
                      />
                      AR Mode (Beta)
                    </label>
                  </div>

                  {/* Tilemap section — designers paint 2D grid-based levels here */}
                  <div className="pt-3 border-t border-[#1E1E24]">
                    <TilemapPainter
                      tilemap={(project?.game_data?.tilemaps || [])[0]}
                      onChange={(tm) => {
                        pushUndo();
                        const tilemaps = [...(project?.game_data?.tilemaps || [])];
                        tilemaps[0] = tm;
                        setProject(prev => ({ ...prev, game_data: { ...prev.game_data, tilemaps }}));
                        toast.success(`Saved tilemap "${tm.name}" (${tm.cols}×${tm.rows})`);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Assets panel — user-facing fal.ai asset gen (RPG prompts) */}
          <AssetsPanel projectId={projectId} onDropToScene={(a) => dropAssetOnScene({ asset_id: a.asset_id, url: a.url, name: a.name, asset_type: a.asset_type, role: a.role }, { offsetX: 0, offsetY: 0 })} />

          {/* AI Copilot - collapsible, auto-expands on activity */}
          <div className={`flex flex-col min-h-0 transition-all duration-200 ${copilotExpanded ? 'flex-[2]' : 'flex-1'}`}>
            <div className="editor-panel-header flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-[#06B6D4]" />
              AI Copilot
              <button
                onClick={() => setCopilotExpanded(v => !v)}
                className="ml-auto text-[10px] text-zinc-500 hover:text-white font-mono"
                data-testid="copilot-toggle-expand"
                title="Expand / collapse chat"
              >
                {copilotExpanded ? '−' : '+'}
              </button>
            </div>
            
            <div ref={copilotScrollRef} className="flex-1 p-3 overflow-y-auto" data-testid="copilot-scroll">
              <div className="space-y-3">
                {copilotMessages.length === 0 && (
                  <div className="text-center py-4">
                    <Bot className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                    <p className="text-xs text-zinc-500">Ask AI to build or edit your game</p>
                    <div className="mt-2 space-y-1 text-[10px] text-zinc-600">
                      <p>&ldquo;Add 5 enemies that patrol between the platforms&rdquo;</p>
                      <p>&ldquo;Make the sky purple and enable fog&rdquo;</p>
                      <p>&ldquo;Add a coin collectible spinning above every platform&rdquo;</p>
                    </div>
                  </div>
                )}
                {copilotMessages.map((msg, idx) => (
                  <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-6 h-6 rounded-full bg-[#06B6D4]/20 flex items-center justify-center shrink-0">
                        <Bot className="w-3 h-3 text-[#06B6D4]" />
                      </div>
                    )}
                    <div className={`max-w-[85%] p-2 rounded-lg text-xs whitespace-pre-wrap break-words leading-snug ${
                      msg.role === 'user' 
                        ? 'bg-[#06B6D4] text-black' 
                        : 'bg-[#121214] border border-[#1E1E24] text-zinc-300'
                    }`}>
                      {msg.content}
                      {msg.action && msg.action !== 'none' && (
                        <span className="block mt-1 text-[10px] text-[#22C55E]">✓ {msg.action}</span>
                      )}
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center shrink-0">
                        <User className="w-3 h-3 text-zinc-300" />
                      </div>
                    )}
                  </div>
                ))}
                {copilotLoading && (
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#06B6D4]/20 flex items-center justify-center">
                      <Bot className="w-3 h-3 text-[#06B6D4]" />
                    </div>
                    <div className="bg-[#121214] border border-[#1E1E24] p-2 rounded-sm">
                      <Loader2 className="w-4 h-4 animate-spin text-[#06B6D4]" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Copilot Input — multi-line textarea so long prompts wrap naturally */}
            <div className="p-2 border-t border-[#1E1E24]">
              <div className="flex gap-1 items-end">
                <textarea
                  placeholder="Ask AI to modify your game… (Shift+Enter for newline, Enter to send)"
                  value={copilotInput}
                  onChange={(e) => { setCopilotInput(e.target.value); if (e.target.value) setCopilotExpanded(true); }}
                  onFocus={() => setCopilotExpanded(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendCopilotMessage();
                    }
                  }}
                  rows={2}
                  className="flex-1 min-h-[38px] max-h-32 bg-[#121214] border border-[#1E1E24] rounded px-2 py-1.5 text-white text-xs resize-y focus:outline-none focus:border-[#06B6D4] font-normal leading-snug whitespace-pre-wrap"
                  disabled={copilotLoading}
                  data-testid="copilot-input"
                />
                <Button 
                  size="icon" 
                  className="h-9 w-9 bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90 shrink-0"
                  onClick={sendCopilotMessage}
                  disabled={copilotLoading || !copilotInput.trim()}
                  data-testid="copilot-send"
                >
                  <Send className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
